import { isPreviewAdminMode, isPreviewProMode, previewParam } from './lib/preview';
import { isViewportDebugOn, toggleViewportDebug } from './lib/viewport-debug';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Map,
  LayoutDashboard,
  Sprout,
  Compass,
  CalendarDays,
  FolderOpen,
  UserRound,
  Users,
  ChevronDown,
  ArrowUpRight,
  ShoppingBag,
  X,
  Plus,
  Trash2,
  Leaf,
  ArrowRight,
  Menu,
  Check,
  ShieldCheck,
  Undo2,
  ShieldAlert,
  Shield,
  Building2,
  Pencil,
  Crosshair,
  Layers,
  HelpCircle,
  Sparkles,
  Navigation,
  Download,
  ChevronUp,
  ChevronLeft,
  ArrowLeft,
  FileText,
  Eye,
  BarChart3,
  Activity,
  Mail,
  ThermometerSun,
  Plug,
  Lock,
  MapPin,
  TreePine,
  LandPlot,
  Smartphone,
  RotateCw,
} from 'lucide-react';

import type {
  AccountType,
  AppState,
  AreaType,
  Estate,
  Field,
  Geometry,
  Page,
  Product,
  Plan,
  SubscriptionTier,
} from './types';
import {
  fieldAreaHa,
  id,
  newEmptyState,
  orderLines,
  selectionIssues,
  formatPackWord,
  isKernelUnit,
  isZoneField,
  isNatureOrZoneField,
  isVildtagerField,
  isOtherPolygonField,
  AREA_TYPES,
  getAreaTypeDefinition,
  getAreaTypeLabel,
  calculateLineLengthMeters,
  getDarkerShade,
  initialState,
  DEFAULT_PRODUCTS,
} from './lib/domain';
import EstateModal from './components/modals/EstateModal';
import UpgradeModal from './components/UpgradeModal';
import { useBillingReturn } from './lib/useBillingReturn';
import UserOnboardingGuide, {
  FloatingTourBar,
  type GuideTier,
  GUIDE_STEPS_FREE,
  GUIDE_STEPS_SEMIPRO,
  GUIDE_STEPS_PRO,
} from './components/UserOnboardingGuide';
import { useWorkspace } from './lib/useWorkspace';
import { configured, supabase } from './lib/supabase';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WorkspaceProvider, useWorkspaceContext } from './context/WorkspaceContext';
import {
  getEstateRole,
  invalidateCloudBaseline,
  loadCloudState,
  loadAccountType,
  saveCloudState,
  recordSiteVisit,
  isDeviceMarkedAdmin,
  setDeviceAdmin,
  ADMIN_EMAILS_LOWER,
  professionalListClients,
  professionalAcceptInvitation,
  professionalRejectInvitation,
  type ProfessionalClient,
  recordFaunamasterClick,
  acceptUserTerms,
  setMarketingConsent,
} from './lib/cloud';
import TermsConsentModal from './components/TermsConsentModal';
import MarketingConsentPrompt from './components/MarketingConsentPrompt';
import RaccoonMapPage from './components/RaccoonMapPage';
import TwoFactorAuthModal from './components/TwoFactorAuthModal';
import LegalTermsModal, { type LegalDocType } from './components/modals/LegalTermsModal';
import SaveGuestPlanModal from './components/modals/SaveGuestPlanModal';
import { hasGuestPlan } from './lib/storage';
import { isMfaRequiredForAccount, isDeviceMfaTrusted } from './lib/mfa';
import { downloadState, downloadOrderCsv, downloadDxf, downloadSurveyorCsv } from './lib/export';
import { blendColor } from './lib/blend-colors';
import {
  customerWorkspaceState,
  mergeCustomerWorkspace,
  type CustomerWorkspace,
} from './lib/customer-workspace';
import { isProfessionalAccount, isProfessionalView, shouldShowAdvanced } from './lib/view-mode';
import LandingPage from './components/LandingPage';
import ProductTour, { TOUR_STEPS } from './components/ProductTour';
import { fieldsForPlan, productsForPlan } from './lib/plans';
import {
  addField,
  editField,
  removeField,
  restoreField as restoreDeletedField,
  type FieldUndo,
} from './lib/fields';
import { recordAdvisorVisit, recordAdvisorAction } from './lib/admin-activity';
import { ErrorBoundary } from './components/ErrorBoundary';
import { determineLandingPage, parseRoute, syncUrlToRoute, type ParsedRoute } from './lib/routing';
import { hasTrustedAdminClaim } from './lib/admin-access';
import {
  parseJagtPoi,
  isJagtPoiField,
  stripJagtPoiPrefix,
  JAGT_POI_CONFIGS,
  formatJagtPoiNotes,
  getFieldCentroid,
  type ShootingDirection,
} from './lib/poi';
import { PoiSoilSensorInspector } from './components/map/PoiSoilSensorInspector';
import { PoiIntegrationInspector } from './components/map/PoiIntegrationInspector';
import {
  unlinkFieldFromIntegrations,
  loadEstateIntegrations,
  saveEstateIntegrations,
} from './lib/integrations';
import { FieldSoilWeatherCard } from './components/map/FieldSoilWeatherCard';
import type { EstateDashboardMode } from './components/workspace/EstateDashboardModeSwitcher';
import { MobileAppShell } from './components/mobile/MobileAppShell';
import { SystemUpdateToast } from './components/SystemUpdateToast';
import { MobilePwaInstallSheet } from './components/mobile/MobilePwaInstallSheet';
import { StandaloneAppUpdateModal } from './components/StandaloneAppUpdateModal';
import { useAppUpdate, CLIENT_BUILD_TIME } from './lib/app-version';
import { PWA_INSTALL_SUSPENDED } from './lib/pwa-install';
import { A11yDialog } from './components/A11yDialog';
import { isCompactViewport } from './lib/viewport';
import { lockBodyScroll, unlockBodyScroll } from './lib/scroll-lock';
const FieldMap = lazy(() => import('./components/FieldMap'));
const WorkspacePages = lazy(() => import('./components/WorkspacePages'));
const TasksPage = lazy(() => import('./components/TasksPage'));
const CatalogPanel = lazy(() => import('./components/CatalogPanel'));
const AccountPanel = lazy(() => import('./components/AccountPanel'));
const SuperAdminDashboard = lazy(() => import('./components/SuperAdminDashboard'));
const SeedAdvisorModal = lazy(() => import('./components/SeedAdvisorModal'));
const ProfessionalClients = lazy(() => import('./components/ProfessionalClients'));
const ForestPanel = lazy(() => import('./components/ForestPanel'));

function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? isCompactViewport(window.innerWidth, window.innerHeight, breakpoint)
      : false,
  );
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(isCompactViewport(window.innerWidth, window.innerHeight, breakpoint));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
}

const number = (value: number, digits = 2) =>
  Number.isFinite(value) ? value.toLocaleString('da-DK', { maximumFractionDigits: digits }) : '0';
const money = (value: number) =>
  new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0);
const packWord = formatPackWord;
const EMPTY_SELECTIONS: Record<string, string> = Object.freeze({});
const nav = [
  { id: 'overview', name: 'Overblik', icon: LayoutDashboard },
  { id: 'map', name: 'Mine marker', icon: Map },
  { id: 'plans', name: 'Plejeplaner', icon: Sprout },
  { id: 'biotope-plan', name: 'Biotopplan', icon: LandPlot },
  { id: 'seeding', name: 'Såningsgennemgang', icon: ThermometerSun },
  { id: 'activities', name: 'Aktiviteter', icon: CalendarDays },
  { id: 'tasks', name: 'Opgaver', icon: Check },
  { id: 'documents', name: 'Dokumenter', icon: FolderOpen },
  { id: 'catalog', name: 'Frøblandinger', icon: Leaf },
  { id: 'forest', name: 'Skovrejsning', icon: Sprout },
  { id: 'integrations', name: 'Integrationer', icon: Plug },
] as const;
export interface DemoOptions {
  page?: Page;
  basket?: boolean;
}

interface AppShellProps {
  demo?: DemoOptions;
  onStartTour?: () => void;
  initialShowLogin?: boolean;
  initialTourActive?: boolean;
  onExitTour?: () => void;
  /** Sand når brugeren netop er vendt tilbage til forsiden fra rundturen (fokus gendannes på "Start rundtur"). */
  returnedFromTour?: boolean;
}

function AppShell({
  demo,
  onStartTour,
  initialShowLogin,
  initialTourActive,
  onExitTour,
  returnedFromTour,
}: AppShellProps = {}) {
  const demoFiles = useRef(new globalThis.Map<string, Blob>());
  const isDemo = Boolean(demo);
  const {
    user,
    isAdmin,
    accountType,
    subscriptionTier,
    accountReady: authReady,
    isDeviceAdmin,
    proClients,
    pendingAdvisorInvite,
    setPendingAdvisorInvite,
    signOut,
  } = useAuth();

  const [mfaPassedFor, setMfaPassedFor] = useState<string | null>(null);
  const isMobile = useIsMobile(768);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalDoc, setLegalDoc] = useState<LegalDocType>('terms');

  const {
    state,
    fullState,
    setState,
    selectedEstateId,
    chooseEstate,
    selectedFieldId,
    setSelectedFieldId,
    isEditingShape,
    setIsEditingShape,
    planReadOnly,
    isProView,
    showAdvanced,
    advanced,
    setAdvanced,
    proMode,
    setProMode,
    saveStatus,
    storageError,
    generation,
    revision,
    cloudDirty,
    cloudConflict,
    cloudError,
    setCloudError,
    cloudBusy,
    cloudGeneration,
    setCloudGeneration,
    reconcileCloud,
    saveAccount,
    reloadAccount,
    customerWorkspace,
    setCustomerWorkspace,
    openCustomer: contextOpenCustomer,
    leaveCustomer: contextLeaveCustomer,
    replaceCustomerState,
  } = useWorkspaceContext();

  const activeCustomer = useRef(customerWorkspace);
  activeCustomer.current = customerWorkspace;
  const currentGeneration = useRef(generation);
  currentGeneration.current = generation;

  function replaceState(update: React.SetStateAction<AppState>) {
    if (typeof update !== 'function') {
      setDeleted(null);
      setSelectedFieldId('');
    }
    setState(update);
  }
  const isPreviewAdmin = isPreviewAdminMode();
  const isPreviewPro = isPreviewProMode();

  const initialRouteRef = useRef<ParsedRoute>(
    typeof window !== 'undefined' ? parseRoute(window.location.hash || window.location.search) : {},
  );
  const initialRoute = initialRouteRef.current;

  const [page, setPage] = useState<Page>(() => {
    if (isPreviewAdmin) return initialRoute.page || 'superadmin';
    if (isPreviewPro) {
      if (initialRoute.page) return initialRoute.page;
      const param = previewParam();
      if (param === 'biotope' || param === 'biotopplan') return 'biotope-plan';
      return 'clients';
    }
    return determineLandingPage({
      isDemo: Boolean(demo),
      routePage: demo?.page || initialRoute.page,
      isRecovery:
        typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).has('recovery'),
    });
  });
  const [enteredWorkspace, setEnteredWorkspace] = useState(() => {
    try {
      if (typeof localStorage !== 'undefined') {
        return localStorage.getItem('plotwise.guest.active') === '1' || hasGuestPlan();
      }
    } catch {
      // ignore
    }
    return false;
  });
  const [showSaveGuestModal, setShowSaveGuestModal] = useState(false);
  const [invitePassword, setInvitePassword] = useState('');
  useEffect(() => {
    if (user?.id) {
      setEnteredWorkspace(false);
      try {
        localStorage.removeItem('plotwise.guest.active');
      } catch {
        // ignore
      }
    }
  }, [user?.id]);
  useEffect(() => {
    if (initialRoute.fieldId) {
      setSelectedFieldId(initialRoute.fieldId);
    }
  }, [initialRoute.fieldId, setSelectedFieldId]);
  const skipResetEditingShapeRef = useRef(false);
  useEffect(() => {
    if (skipResetEditingShapeRef.current) {
      skipResetEditingShapeRef.current = false;
      return;
    }
    setIsEditingShape(false);
  }, [selectedFieldId, setIsEditingShape]);
  useEffect(() => {
    if (!selectedFieldId) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (document.querySelector('dialog[open]')) return;
        const target = e.target as HTMLElement | null;
        if (
          target &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.tagName === 'SELECT')
        ) {
          target.blur();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        setSelectedFieldId('');
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedFieldId]);
  const selectField = (id: string, startEditing = false) => {
    if (startEditing) {
      skipResetEditingShapeRef.current = true;
      setSelectedFieldId(id);
      setIsEditingShape(true);
    } else {
      setSelectedFieldId(id);
    }
    if (id) {
      setIsFieldPanelCollapsed(false);
      setTimeout(() => {
        const targetEl = document.querySelector(
          '.field-inspector, .poi-soil-sensor-inspector, .poi-integration-inspector, .field-item.is-selected',
        );
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 60);
    }
  };

  // Sikrer automatisk visning og scroll til mark- eller POI-inspektøren ved valg på kortet
  useEffect(() => {
    if (!selectedFieldId) return;
    setIsFieldPanelCollapsed(false);
    const timer = setTimeout(() => {
      const targetEl = document.querySelector(
        '.field-inspector, .poi-soil-sensor-inspector, .poi-integration-inspector, .field-item.is-selected',
      );
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [selectedFieldId]);
  useEffect(() => {
    if (isDemo) return;
    const isUserAdmin =
      isDeviceMarkedAdmin() ||
      (user?.email && ADMIN_EMAILS_LOWER.includes(user.email.toLowerCase()));
    if (isUserAdmin) return;

    const visitedPage = !user && !enteredWorkspace ? 'landing' : page;
    if (
      [
        'superadmin',
        'admin-activity',
        'admin-email',
        'admin-traffic',
        'admin-stats',
        'admin-users',
      ].includes(visitedPage)
    ) {
      setDeviceAdmin(true);
      return;
    }

    recordSiteVisit(visitedPage, !user, user?.email, user?.id, {
      analyticsConsent: state.profile.analytics_consent,
    });

    // Mål aktiv sessionstid hvert 30. sekund når siden er synlig
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        if (
          isDeviceMarkedAdmin() ||
          (user?.email && ADMIN_EMAILS_LOWER.includes(user.email.toLowerCase()))
        ) {
          return;
        }
        const curPage = !user && !enteredWorkspace ? 'landing' : page;
        if (
          [
            'superadmin',
            'admin-activity',
            'admin-email',
            'admin-traffic',
            'admin-stats',
            'admin-users',
          ].includes(curPage)
        ) {
          return;
        }
        recordSiteVisit(curPage, !user, user?.email, user?.id, {
          heartbeatSeconds: 30,
          analyticsConsent: state.profile.analytics_consent,
        });
      }
    }, 30000);

    return () => clearInterval(timer);
  }, [page, user?.id, user?.email, enteredWorkspace, isDemo]);
  const [basket, setBasket] = useState(false);
  useEffect(() => {
    if (demo) setBasket(Boolean(demo.basket));
  }, [demo?.basket]);
  const [shopBusy, setShopBusy] = useState(false);
  const [shopMessage, setShopMessage] = useState('');
  const shopScope = useRef('');
  shopScope.current = `${generation}:${user?.id ?? 'guest'}:${state.selectedPlanId}`;
  const [mobileNav, setMobileNav] = useState(false);
  const [notice, setNotice] = useState('');
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [advisorTargetFieldId, setAdvisorTargetFieldId] = useState<string>('');
  const isGuest = !user && !isDemo && !isPreviewAdmin;
  const saveLabel = isDemo
    ? 'Demo · intet gemmes'
    : isGuest
      ? 'Gæst · gemmes lokalt'
      : saveStatus === 'saved'
        ? 'Gemt lokalt'
        : saveStatus === 'saving'
          ? 'Gemmer lokalt…'
          : 'Ikke gemt';
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const isProfessional = accountType === 'professional';
  const allowEstateCreation = !isGuest && (!customerWorkspace || isProfessional || isAdmin);
  const now = () => new Date().toISOString();
  const [blendFieldId, setBlendFieldId] = useState('');
  const [adminWorkspaceMode, setAdminWorkspaceMode] = useState<'admin' | 'advisor'>(() => {
    if (initialRoute.page) {
      if (
        [
          'superadmin',
          'admin-seeds',
          'admin-map',
          'admin-activity',
          'admin-email',
          'admin-traffic',
          'admin-stats',
        ].includes(initialRoute.page)
      ) {
        return 'admin';
      }
      if (
        [
          'map',
          'plans',
          'overview',
          'catalog',
          'raccoon-map',
          'tasks',
          'activities',
          'documents',
          'forest',
        ].includes(initialRoute.page)
      ) {
        return 'advisor';
      }
    }
    try {
      const saved = localStorage.getItem('plotwise.adminWorkspaceMode');
      if (saved === 'admin' || saved === 'advisor') return saved;
    } catch {}
    return 'admin';
  });
  const [estateDashboardMode, setEstateDashboardMode] = useState<EstateDashboardMode>(() => {
    if (!isAdmin) {
      return subscriptionTier === 'pro'
        ? 'pro'
        : subscriptionTier === 'semipro'
          ? 'semipro'
          : 'simple';
    }
    try {
      const saved = localStorage.getItem('plotwise.estateDashboardMode');
      if (saved === 'simple' || saved === 'semipro' || saved === 'pro') return saved;
    } catch {}
    return 'pro';
  });

  // Synkroniser standard estateDashboardMode med brugerens reelle subscriptionTier for almindelige brugere
  useEffect(() => {
    if (!isAdmin) {
      const targetMode: EstateDashboardMode =
        subscriptionTier === 'pro' ? 'pro' : subscriptionTier === 'semipro' ? 'semipro' : 'simple';
      setEstateDashboardMode(targetMode);
    }
  }, [isAdmin, subscriptionTier]);

  const isAdvisorWorkspace = isAdmin && !customerWorkspace && adminWorkspaceMode === 'advisor';
  const customerTier: SubscriptionTier | undefined = customerWorkspace?.tier;

  const isFreeAccount = customerWorkspace
    ? !isProfessional && !isAdmin && (customerTier === 'free' || !customerTier)
    : isAdvisorWorkspace
      ? estateDashboardMode === 'simple'
      : (subscriptionTier
          ? subscriptionTier === 'free'
          : !isProfessionalAccount({ accountType, isAdmin })) && !isDemo;

  const isSemiPro = customerWorkspace
    ? customerTier === 'semipro'
    : isAdvisorWorkspace
      ? estateDashboardMode === 'semipro'
      : subscriptionTier === 'semipro';

  const effectiveIsProView = customerWorkspace
    ? isProfessional || isAdmin || customerTier === 'pro'
    : isAdvisorWorkspace
      ? estateDashboardMode === 'pro'
      : isProView;

  const isProTier = customerWorkspace
    ? isProfessional || isAdmin || customerTier === 'pro'
    : isAdvisorWorkspace
      ? estateDashboardMode === 'pro'
      : subscriptionTier === 'pro' || isProfessional || effectiveIsProView;

  // Rådgivere og administratorer har altid fuld Pro-adgang til tegneværktøjer, også i kundearbejdspladser
  const canAccessPro = customerWorkspace
    ? isProfessional || isAdmin || customerTier === 'pro'
    : isAdvisorWorkspace
      ? estateDashboardMode === 'pro'
      : isProTier || isProfessional || isAdmin || isDemo;

  const canAccessSemiPro = customerWorkspace
    ? isProfessional || isAdmin || customerTier === 'semipro' || customerTier === 'pro'
    : isAdvisorWorkspace
      ? estateDashboardMode === 'semipro' || estateDashboardMode === 'pro'
      : canAccessPro || isSemiPro || isDemo;

  const [upgradeModal, setUpgradeModal] = useState<{
    isOpen: boolean;
    featureName: string;
    requiredTier: 'semipro' | 'pro';
    description?: string;
  } | null>(null);

  const openUpgradeModal = (
    featureName: string,
    requiredTier: 'semipro' | 'pro',
    description?: string,
  ) => {
    setUpgradeModal({
      isOpen: true,
      featureName,
      requiredTier,
      description,
    });
  };

  const guideUserTier: GuideTier = customerWorkspace
    ? customerTier === 'pro'
      ? 'pro'
      : customerTier === 'semipro'
        ? 'semipro'
        : 'free'
    : isAdvisorWorkspace
      ? estateDashboardMode === 'pro'
        ? 'pro'
        : estateDashboardMode === 'semipro'
          ? 'semipro'
          : 'free'
      : isProTier
        ? 'pro'
        : isSemiPro
          ? 'semipro'
          : 'free';

  const [guideModalOpen, setGuideModalOpen] = useState(false);
  const [pwaSheetOpen, setPwaSheetOpen] = useState(false);
  const [tourActive, setTourActive] = useState(() => Boolean(initialTourActive));
  const [tourStep, setTourStep] = useState(0);
  const [tourTier, setTourTier] = useState<GuideTier>('free');
  const [tourCollapsed, setTourCollapsed] = useState(false);
  const [isDrawingMap, setIsDrawingMap] = useState(false);
  const [cadastralEnabled, setCadastralEnabled] = useState(false);
  const [markblokkeEnabled, setMarkblokkeEnabled] = useState(false);
  const [guideDismissed, setGuideDismissed] = useState<boolean>(() => {
    try {
      return (
        localStorage.getItem('plotwise.guide_dismissed') === 'true' ||
        localStorage.getItem('plotwise_guide_dismissed') === 'true'
      );
    } catch {
      return false;
    }
  });

  const handleStartTour = (tier: GuideTier) => {
    setTourTier(tier);
    setTourStep(0);
    setTourCollapsed(false);
    setTourActive(true);
    setGuideModalOpen(false);
    const steps =
      tier === 'pro'
        ? GUIDE_STEPS_PRO
        : tier === 'semipro'
          ? GUIDE_STEPS_SEMIPRO
          : GUIDE_STEPS_FREE;
    if (page !== steps[0].page) {
      navigate(steps[0].page);
    }
  };

  const handleTourNext = () => {
    const steps =
      tourTier === 'pro'
        ? GUIDE_STEPS_PRO
        : tourTier === 'semipro'
          ? GUIDE_STEPS_SEMIPRO
          : GUIDE_STEPS_FREE;
    if (tourStep < steps.length - 1) {
      const nextIndex = tourStep + 1;
      setTourStep(nextIndex);
      const targetPage = steps[nextIndex].page;
      if (page !== targetPage) {
        navigate(targetPage);
      }
    } else {
      setTourActive(false);
      try {
        localStorage.setItem('plotwise.guide_completed', 'true');
        localStorage.setItem('plotwise.guide_dismissed', 'true');
        localStorage.setItem('plotwise_guide_dismissed', 'true');
      } catch {}
      setGuideDismissed(true);
      notify('Godt klaret! Du er nu klar til at forvalte dit revir.');
    }
  };

  const handleTourPrev = () => {
    const steps =
      tourTier === 'pro'
        ? GUIDE_STEPS_PRO
        : tourTier === 'semipro'
          ? GUIDE_STEPS_SEMIPRO
          : GUIDE_STEPS_FREE;
    if (tourStep > 0) {
      const prevIndex = tourStep - 1;
      setTourStep(prevIndex);
      const targetPage = steps[prevIndex].page;
      if (page !== targetPage) {
        navigate(targetPage);
      }
    }
  };

  const handleTourAction = (actionType: string) => {
    if (actionType === 'fullscreen') {
      if (isMobile) {
        if (page !== 'map') navigate('map');
        notify('Kortet er allerede i 100% fuldskærm på mobilen.');
        handleTourNext();
      } else {
        setMapFullscreen((prev) => !prev);
      }
    } else if (actionType === 'draw_field') {
      if (page !== 'map') navigate('map');
      setDrawRequest('field');
    } else if (actionType === 'try_markblok') {
      if (page !== 'map') navigate('map');
      setTimeout(() => {
        const btn = document.querySelector('[data-guide="action-markblok"]') as HTMLElement | null;
        btn?.click();
      }, 120);
    } else if (actionType === 'try_cadastral') {
      if (page !== 'map') navigate('map');
      setTimeout(() => {
        const layerBtn = document.querySelector('[data-guide="btn-kortlag"]') as HTMLElement | null;
        layerBtn?.click();
        setTimeout(() => {
          const propertySubpageBtn = document.querySelector(
            '[data-guide="subpage-property"]',
          ) as HTMLElement | null;
          if (propertySubpageBtn) {
            propertySubpageBtn.click();
            setTimeout(() => {
              const matrikelskelBtn = document.querySelector(
                '[data-guide="layer-matrikelskel"]',
              ) as HTMLElement | null;
              matrikelskelBtn?.click();
            }, 100);
          } else {
            const matrikelskelBtn = document.querySelector(
              '[data-guide="layer-matrikelskel"]',
            ) as HTMLElement | null;
            matrikelskelBtn?.click();
          }
        }, 150);
      }, 120);
    } else if (actionType === 'try_arealer') {
      if (page !== 'map') navigate('map');
      setTimeout(() => {
        const arealerBtn = document.querySelector(
          '[data-guide="action-arealer"]',
        ) as HTMLElement | null;
        arealerBtn?.click();
      }, 120);
    } else if (actionType === 'try_punkter') {
      if (page !== 'map') navigate('map');
      setTimeout(() => {
        const punkterBtn = document.querySelector(
          '[data-guide="action-punkter"]',
        ) as HTMLElement | null;
        punkterBtn?.click();
      }, 120);
    } else if (actionType === 'navigate') {
      const steps =
        tourTier === 'pro'
          ? GUIDE_STEPS_PRO
          : tourTier === 'semipro'
            ? GUIDE_STEPS_SEMIPRO
            : GUIDE_STEPS_FREE;
      const current = steps[tourStep];
      if (current && current.page && page !== current.page) {
        navigate(current.page);
      }
    }
  };

  const handleDismissGuide = () => {
    setGuideDismissed(true);
    try {
      localStorage.setItem('plotwise.guide_dismissed', 'true');
      localStorage.setItem('plotwise_guide_dismissed', 'true');
    } catch {}
  };
  const effectiveShowAdvanced = customerWorkspace
    ? isProfessional || isAdmin || customerTier === 'semipro' || customerTier === 'pro'
    : effectiveIsProView || estateDashboardMode === 'semipro'
      ? true
      : isAdvisorWorkspace
        ? false
        : showAdvanced;

  const handleSelectDashboardMode = (mode: EstateDashboardMode) => {
    setEstateDashboardMode(mode);
    try {
      localStorage.setItem('plotwise.estateDashboardMode', mode);
    } catch {}

    if (mode === 'simple') {
      const advancedPages: Page[] = [
        'plans',
        'seeding',
        'activities',
        'tasks',
        'documents',
        'forest',
        'clients',
      ];
      if (advancedPages.includes(page)) {
        navigate('overview');
      }
    } else if (mode === 'semipro') {
      const proOnlyPages: Page[] = ['clients', 'advisor', 'forest'];
      if (proOnlyPages.includes(page)) {
        navigate('overview');
      }
    } else if (mode === 'pro') {
      const proDisallowedPages: Page[] = ['forest'];
      if (proDisallowedPages.includes(page)) {
        navigate('overview');
      }
    }
  };

  const administration = isAdmin && !customerWorkspace && adminWorkspaceMode === 'admin';
  const visibleState = useMemo(() => {
    if (customerWorkspace) {
      return customerWorkspaceState(state, customerWorkspace.estateIds);
    }
    if (isAdvisorWorkspace && user?.id) {
      const own = state.estates.filter((e) => !e.ownerUserId || e.ownerUserId === user.id);
      const ownIds = new Set(own.map((e) => e.id));
      const selEstateId = ownIds.has(state.selectedEstateId)
        ? state.selectedEstateId
        : (own[0]?.id ?? '');
      const ownPlans = state.plans.filter((p) => ownIds.has(p.estateId));
      const selPlanId = ownPlans.some((p) => p.id === state.selectedPlanId)
        ? state.selectedPlanId
        : (ownPlans.find((p) => p.estateId === selEstateId)?.id ?? ownPlans[0]?.id ?? '');

      return {
        ...state,
        estates: own,
        fields: state.fields.filter((f) => ownIds.has(f.estateId)),
        plans: ownPlans,
        activities: state.activities.filter((a) => ownIds.has(a.estateId)),
        tasks: state.tasks.filter((t) => ownIds.has(t.estateId)),
        attachments: state.attachments.filter((att) => ownIds.has(att.estateId)),
        selectedEstateId: selEstateId,
        selectedPlanId: selPlanId,
      };
    }
    return state;
  }, [state, customerWorkspace, isAdvisorWorkspace, user?.id]);

  const [deleted, setDeleted] = useState<FieldUndo | null>(null);
  const estate = visibleState.estates.find((e) => e.id === visibleState.selectedEstateId);
  const plan = visibleState.plans.find((p) => p.id === visibleState.selectedPlanId);
  const rawFields = useMemo(
    () => fieldsForPlan(visibleState, plan),
    [visibleState.fields, plan?.snapshot?.fields, plan?.estateId],
  );
  // Almindelige/gratis kunder ser KUN godkendte driftsmarker; rådgiverkladder (advisorOnly === true) er skjult
  const isProOrAdmin = isProfessional || isAdmin;
  const fields = useMemo(() => {
    if (isProOrAdmin) return rawFields;
    return rawFields.filter((f) => !f.advisorOnly);
  }, [rawFields, isProOrAdmin]);
  const products = useMemo(
    () => productsForPlan(visibleState, plan),
    [visibleState.products, plan?.snapshot?.products, plan?.status],
  );
  const planSelections = useMemo(() => plan?.selections ?? EMPTY_SELECTIONS, [plan?.selections]);
  const canToggleAdvanced =
    !isAdvisorWorkspace &&
    estateDashboardMode !== 'semipro' &&
    estateDashboardMode !== 'pro' &&
    !isFreeAccount &&
    (isProfessionalAccount({ accountType, isAdmin, proMode }) ||
      isDemo ||
      (!user && enteredWorkspace)) &&
    !administration &&
    !effectiveIsProView;
  const selected = fields.find((f) => f.id === selectedFieldId);
  const issues = useMemo(() => selectionIssues(fields, plan, products), [fields, plan, products]);
  const lines = useMemo(
    () => (isFreeAccount && issues.length ? [] : orderLines(fields, plan, products)),
    [fields, plan, products, isFreeAccount, issues],
  );
  const vildtagerFields = useMemo(() => fields.filter((f) => !isNatureOrZoneField(f)), [fields]);
  const zoneFields = useMemo(
    () => fields.filter((f) => isNatureOrZoneField(f) && !isJagtPoiField(f)),
    [fields],
  );
  const totalArea = useMemo(
    () =>
      fields.filter((f) => !isJagtPoiField(f)).reduce((sum, f) => sum + fieldAreaHa(f.geometry), 0),
    [fields],
  );
  const totalVildtagerArea = useMemo(
    () => vildtagerFields.reduce((sum, f) => sum + fieldAreaHa(f.geometry), 0),
    [vildtagerFields],
  );
  const totalZoneArea = useMemo(
    () => zoneFields.reduce((sum, f) => sum + fieldAreaHa(f.geometry), 0),
    [zoneFields],
  );
  const [openNewEstateOnOverview, setOpenNewEstateOnOverview] = useState(false);
  const [isCreateEstateModalOpen, setIsCreateEstateModalOpen] = useState(false);
  const [showAdvisorModal, setShowAdvisorModal] = useState(false);
  const [triggerGpsModal, setTriggerGpsModal] = useState(false);
  // Mobil: naar markoplysningerne daekker handlingslinjen, startes tegningen herfra.
  const [drawRequest, setDrawRequest] = useState<'field' | 'zone' | null>(null);
  const [isFieldPanelCollapsed, setIsFieldPanelCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<{
    vildtagre: boolean;
    oevrige: boolean;
    kortpunkter: boolean;
  }>({
    vildtagre: true,
    oevrige: true,
    kortpunkter: true,
  });

  const toggleSection = (section: 'vildtagre' | 'oevrige' | 'kortpunkter') => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  useEffect(() => {
    if (!selectedFieldId) return;
    const sel = fields.find((f) => f.id === selectedFieldId);
    if (!sel) return;
    if (isJagtPoiField(sel)) {
      setExpandedSections((prev) => (prev.kortpunkter ? prev : { ...prev, kortpunkter: true }));
    } else if (isOtherPolygonField(sel)) {
      setExpandedSections((prev) => (prev.oevrige ? prev : { ...prev, oevrige: true }));
    } else if (isVildtagerField(sel)) {
      setExpandedSections((prev) => (prev.vildtagre ? prev : { ...prev, vildtagre: true }));
    }
  }, [selectedFieldId, fields]);

  async function handleSaveNewEstate(data: {
    name: string;
    address: string;
    center: [number, number];
  }) {
    const next: Estate = {
      id: id(),
      name: data.name,
      address: data.address,
      center: data.center,
      createdAt: now(),
      ownerUserId: customerWorkspace ? customerWorkspace.userId : user?.id,
    };
    const plan: Plan = {
      id: id(),
      estateId: next.id,
      name: `Plejeplan ${new Date().getFullYear()}`,
      year: new Date().getFullYear(),
      status: 'draft',
      notes: '',
      selections: {},
      updatedAt: now(),
    };
    if (customerWorkspace && setCustomerWorkspace) {
      setCustomerWorkspace((cw) => (cw ? { ...cw, estateIds: [...cw.estateIds, next.id] } : null));
    }
    const updateState = customerWorkspace ? replaceCustomerState : replaceState;
    updateState((s) => ({
      ...s,
      estates: [...s.estates, next],
      plans: [...s.plans, plan],
      selectedEstateId: next.id,
      selectedPlanId: plan.id,
    }));
    chooseEstate(next.id);
    setIsCreateEstateModalOpen(false);
    notify(`Reviret "${next.name}" er oprettet og valgt på kortet.`);
  }

  /** Luk markoplysningerne og bed kortet begynde at tegne — saa skemaet ikke daekker kortet. */
  function startDrawingFromInspector(kind: 'field' | 'zone') {
    setIsEditingShape(false);
    setSelectedFieldId('');
    setDrawRequest(kind);
  }

  // Synkroniser landingsside for nyligt loggede brugere baseret på initial landing route
  const initialLandingAppliedRef = useRef(false);
  useEffect(() => {
    if (!user || isDemo || isPreviewPro) return;
    if (new URLSearchParams(window.location.search).has('recovery')) return;
    if (initialRoute.page) return;
    if (initialLandingAppliedRef.current) return;

    if (isAdmin) {
      initialLandingAppliedRef.current = true;
      setPage('superadmin');
    } else if (accountType === 'professional') {
      initialLandingAppliedRef.current = true;
      setPage('clients');
    } else if (accountType === 'standard' && authReady) {
      initialLandingAppliedRef.current = true;
      setPage('map');
    }
  }, [user?.id, isAdmin, accountType, authReady, isDemo, initialRoute.page]);

  useEffect(() => {
    if (isDemo || !supabase) return;
    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setPage('profile');
    });
    return () => data.subscription.unsubscribe();
  }, [isDemo]);

  // Ensure selected estate always has a plan
  useEffect(() => {
    if (estate && !plan && (!user || !configured || cloudGeneration === generation)) {
      const year = new Date().getFullYear();
      const newPlan: Plan = {
        id: id(),
        estateId: estate.id,
        name: `Plejeplan ${year}`,
        year,
        status: 'draft',
        notes: '',
        selections: {},
        updatedAt: new Date().toISOString(),
      };
      setState((s) => ({
        ...s,
        plans: [...s.plans, newPlan],
        selectedPlanId: newPlan.id,
      }));
    }
  }, [estate?.id, plan?.id, user?.id, cloudGeneration, generation]);

  const notify = useCallback((message: string) => {
    setNotice(message);
  }, []);

  useBillingReturn(Boolean(user), notify);

  const {
    isStandalone: isRunningInApp,
    hasUpdate: hasAppUpdate,
    showUpdateModal: showAppUpdateModal,
    setShowUpdateModal: setShowAppUpdateModal,
    isChecking: isCheckingAppUpdate,
    isUpdating: isUpdatingApp,
    updateInfo: appUpdateInfo,
    effectiveUpdateTime,
    lastUpdatedLabel,
    checkNow: checkAppUpdateNow,
    applyUpdate: applyAppUpdate,
    dismissUpdateModal: dismissAppUpdateModal,
  } = useAppUpdate({
    user,
    onUpdateDetected: () => {
      notify('Ny version af Plotwise er klar til opdatering!');
    },
  });

  function navigate(next: Page) {
    if (
      !isAdmin &&
      (next === 'superadmin' ||
        next === 'admin-overview' ||
        next === 'admin-actions' ||
        next === 'admin-estates' ||
        next === 'admin-seeds' ||
        next === 'admin-map' ||
        next === 'admin-activity' ||
        next === 'admin-email' ||
        next === 'admin-traffic' ||
        next === 'admin-stats')
    )
      return;
    if (next === 'clients' && !canAccessPro) {
      openUpgradeModal(
        'Mine kunder',
        'pro',
        'Få samlet kundestyring, adgang til kunders revirer og professionelle rådgivningsrapporter.',
      );
      return;
    }
    if (
      ['plans', 'seeding', 'activities', 'tasks', 'documents'].includes(next) &&
      !canAccessSemiPro
    ) {
      const labels: Record<string, string> = {
        plans: 'Plejeplaner',
        seeding: 'Såningsgennemgang',
        activities: 'Aktiviteter & Opgaver',
        tasks: 'Aktiviteter & Opgaver',
        documents: 'Dokumenter',
      };
      openUpgradeModal(
        labels[next] || next,
        'semipro',
        'Få adgang til udvidede revirværktøjer og planlægning med Semi-pro.',
      );
      return;
    }
    if (['forest', 'integrations', 'biotope-plan'].includes(next) && !canAccessPro) {
      const labels: Record<string, string> = {
        forest: 'Skovrejsning',
        integrations: 'Integrationer',
        'biotope-plan': 'Biotopplaner',
      };
      openUpgradeModal(
        labels[next] || next,
        'pro',
        'Få adgang til lovpligtig biotopplanlægning, pointberegner og kvoteoverblik med Professionel.',
      );
      return;
    }
    if (isAdmin && !customerWorkspace) {
      if (
        [
          'superadmin',
          'admin-overview',
          'admin-actions',
          'admin-estates',
          'admin-seeds',
          'admin-map',
          'admin-activity',
          'admin-email',
          'admin-traffic',
          'admin-stats',
        ].includes(next)
      ) {
        setAdminWorkspaceMode('admin');
        try {
          localStorage.setItem('plotwise.adminWorkspaceMode', 'admin');
        } catch {}
      } else if (
        [
          'map',
          'plans',
          'overview',
          'catalog',
          'raccoon-map',
          'tasks',
          'activities',
          'documents',
          'forest',
          'clients',
          'seeding',
          'integrations',
        ].includes(next)
      ) {
        setAdminWorkspaceMode('advisor');
        try {
          localStorage.setItem('plotwise.adminWorkspaceMode', 'advisor');
        } catch {}
      }
    }
    if (isAdmin && next === 'profile') leaveCustomer();
    setMapFullscreen(false);
    setPage(next);
    setMobileNav(false);
  }
  useEffect(() => {
    if (!demo?.page) return;
    if (['plans', 'activities', 'documents'].includes(demo.page)) setAdvanced(true);
    navigate(demo.page);
  }, [demo?.page]);

  const openCustomer = (
    customer: CustomerWorkspace,
    estateId: string,
    estateMeta?: { id: string; name: string; address?: string; center?: [number, number] },
    targetPage: Page = 'customer-hub',
  ) => {
    contextOpenCustomer(customer, estateId, estateMeta, targetPage);
    const targetMode: EstateDashboardMode =
      customer.tier === 'pro' ? 'pro' : customer.tier === 'semipro' ? 'semipro' : 'simple';
    setEstateDashboardMode(targetMode);
    try {
      localStorage.setItem('plotwise.estateDashboardMode', targetMode);
    } catch {}
    setPage(targetPage);
    setMobileNav(false);
  };

  const leaveCustomer = () => {
    contextLeaveCustomer();
    setMapFullscreen(false);
    setAdminWorkspaceMode('admin');
    setPage(isAdmin || isPreviewAdmin ? 'superadmin' : isProfessional ? 'clients' : 'overview');
    setMobileNav(false);
  };
  useEffect(() => {
    if (!isDemo && !authReady) return;
    if (
      !isAdmin &&
      !isPreviewAdmin &&
      (page === 'superadmin' ||
        page === 'admin-overview' ||
        page === 'admin-actions' ||
        page === 'admin-estates' ||
        page === 'admin-seeds' ||
        page === 'admin-map' ||
        page === 'admin-activity' ||
        page === 'admin-email' ||
        page === 'admin-traffic' ||
        page === 'admin-stats')
    ) {
      setPage('map');
      return;
    }
    if (
      (page === 'clients' || page === 'biotope-plan' || page === 'customer-hub') &&
      !canAccessPro
    ) {
      setPage('map');
      return;
    }
    // Samme niveaukrav som i navigate(), så et dyb-link eller en genindlæsning ikke omgår betalingen.
    if (
      ['plans', 'seeding', 'activities', 'tasks', 'documents'].includes(page) &&
      !canAccessSemiPro
    ) {
      setPage('map');
      return;
    }
    if (['forest', 'integrations'].includes(page) && !canAccessPro) {
      setPage('map');
      return;
    }
    if (page === 'customer-hub' && !customerWorkspace) {
      setPage('clients');
      return;
    }
    if (
      (isAdmin || isPreviewAdmin) &&
      !customerWorkspace &&
      adminWorkspaceMode === 'admin' &&
      ![
        'superadmin',
        'admin-seeds',
        'admin-map',
        'catalog',
        'raccoon-map',
        'admin-activity',
        'admin-email',
        'admin-traffic',
        'admin-stats',
        'profile',
      ].includes(page)
    )
      setPage('superadmin');
  }, [
    authReady,
    demo,
    isAdmin,
    canAccessPro,
    canAccessSemiPro,
    customerWorkspace,
    adminWorkspaceMode,
    page,
    accountType,
    visibleState.estates.length,
  ]);
  useEffect(() => {
    if (!customerWorkspace) return;
    if (!visibleState.estates.length) {
      leaveCustomer();
      notify('Kundens revirer er ikke længere tilgængelige. Administrationen er åbnet.');
    } else if (state.selectedEstateId !== visibleState.selectedEstateId) {
      chooseEstate(visibleState.selectedEstateId);
    }
  }, [
    customerWorkspace,
    state.selectedEstateId,
    visibleState.selectedEstateId,
    visibleState.estates.length,
  ]);
  useEffect(() => {
    setBasket(false);
    setSelectedFieldId('');
    setBlendFieldId('');
    setDeleted(null);
    setOverrides({});
    setChecks({});
    setShopMessage('');
    setShopBusy(false);
  }, [state.selectedEstateId, state.selectedPlanId, generation]);
  useEffect(() => {
    if (!mobileNav) return;
    const media = window.matchMedia('(max-width: 880px)');
    if (!media.matches) {
      setMobileNav(false);
      return;
    }
    const opener = document.querySelector<HTMLElement>('.mobile-menu');
    const sidebar = document.querySelector<HTMLElement>('.sidebar');
    lockBodyScroll();
    sidebar?.querySelector<HTMLElement>('button')?.focus();
    function key(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setMobileNav(false);
        return;
      }
      if (event.key !== 'Tab' || !sidebar) return;
      const controls = Array.from(
        sidebar.querySelectorAll<HTMLElement>('button:not(:disabled),a[href]'),
      ).filter((element) => element.offsetParent !== null);
      const first = controls[0],
        last = controls.at(-1);
      if (!sidebar.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    function resized() {
      if (!media.matches) setMobileNav(false);
    }
    document.addEventListener('keydown', key);
    media.addEventListener('change', resized);
    return () => {
      unlockBodyScroll();
      document.removeEventListener('keydown', key);
      media.removeEventListener('change', resized);
      if (media.matches) opener?.focus();
    };
  }, [mobileNav]);
  useEffect(() => {
    setMobileNav(false);
  }, [generation]);
  useEffect(() => {
    if (!basket) return;
    setMobileNav(false);
    lockBodyScroll();
    const previous = document.activeElement as HTMLElement | null;
    const dialog = document.querySelector<HTMLElement>('.order-drawer');
    dialog?.querySelector<HTMLElement>('button')?.focus();
    function key(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setBasket(false);
        return;
      }
      if (event.key !== 'Tab' || !dialog) return;
      const controls = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled),a[href],input:not(:disabled),select,textarea',
        ),
      ).filter((el) => el.offsetParent !== null);
      const first = controls[0],
        last = controls.at(-1);
      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener('keydown', key);
    return () => {
      unlockBodyScroll();
      document.removeEventListener('keydown', key);
      previous?.focus();
    };
  }, [basket]);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(''), 3500);
    return () => clearTimeout(timer);
  }, [notice]);

  // Synkronisér aktivt revir og mark fra URL ved opstart eller når revirer er indlæst
  const initialSyncDoneRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (initialSyncDoneRef.current) return;
    if (state.estates.length === 0) return;
    initialSyncDoneRef.current = true;

    const currentRoute = parseRoute(window.location.hash || window.location.search);
    if (currentRoute.estateId && state.estates.some((e) => e.id === currentRoute.estateId)) {
      if (state.selectedEstateId !== currentRoute.estateId) {
        chooseEstate(currentRoute.estateId);
      }
    }
    if (currentRoute.fieldId && currentRoute.fieldId !== selectedFieldId) {
      setSelectedFieldId(currentRoute.fieldId);
    }
  }, [state.estates]);

  // URL-synkronisering: Opdater browserens URL (hash) med aktuel tilstand
  useEffect(() => {
    if (isDemo) return;
    if (!user && !enteredWorkspace && !isPreviewAdmin) return;
    const isStandalonePage = [
      'superadmin',
      'admin-seeds',
      'admin-map',
      'admin-activity',
      'admin-email',
      'admin-traffic',
      'admin-stats',
      'profile',
      'clients',
      'catalog',
      'raccoon-map',
    ].includes(page);

    const estateIdToSync = isStandalonePage ? undefined : state.selectedEstateId || undefined;
    const fieldIdToSync = isStandalonePage ? undefined : selectedFieldId || undefined;

    syncUrlToRoute({
      estateId: estateIdToSync,
      page,
      fieldId: fieldIdToSync,
    });
  }, [page, state.selectedEstateId, selectedFieldId, user, enteredWorkspace, demo, isPreviewAdmin]);

  // Lyt på hashchange event så browserens frem/tilbage-knapper opdaterer tilstanden
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onHashChange() {
      const route = parseRoute(window.location.hash);
      if (route.page && route.page !== page) {
        navigate(route.page);
      }
      if (route.estateId && route.estateId !== state.selectedEstateId) {
        if (state.estates.some((e) => e.id === route.estateId)) {
          chooseEstate(route.estateId);
        }
      }
      if (route.fieldId !== undefined && route.fieldId !== selectedFieldId) {
        setSelectedFieldId(route.fieldId);
      } else if (!route.fieldId && selectedFieldId) {
        setSelectedFieldId('');
      }
    }

    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [page, state.selectedEstateId, state.estates, selectedFieldId]);

  // Fokusér på markør hvis man ankommer fra f.eks. Integrationer ('Vis på kortet')
  useEffect(() => {
    if (page === 'map' && typeof window !== 'undefined') {
      try {
        const pendingFocus = sessionStorage.getItem('plotwise.pendingFocusFieldId');
        if (pendingFocus) {
          sessionStorage.removeItem('plotwise.pendingFocusFieldId');
          setSelectedFieldId(pendingFocus);
        }
      } catch {}
    }
  }, [page, setSelectedFieldId]);
  function batchImportGisFields(importedFields: Field[]) {
    if (!estate || !plan) {
      notify('Opret først revir og plan før du importerer GIS-data.');
      return;
    }
    const withEstate = importedFields.map((f) => ({
      ...f,
      estateId: estate.id,
    }));
    setState((s) => ({
      ...s,
      fields: [...s.fields, ...withEstate],
    }));
  }

  function createField(
    geometry: Geometry,
    kind: 'field' | 'zone' = 'field',
    areaType?: AreaType,
    customName?: string,
    customNotes?: string,
    lineLengthMeters?: number,
    lineWidthMeters?: number,
  ): { ok: boolean; fieldId?: string; error?: string } {
    if (activeCustomer.current !== customerWorkspace) {
      return { ok: false, error: 'Kundearbejdspladsen er ændret under arbejdsgangen.' };
    }
    if (planReadOnly) {
      const msg = 'Kopiér planen for at ændre marker.';
      notify(msg);
      return { ok: false, error: msg };
    }
    if (!estate || !plan) {
      const msg = 'Opret først revir og plan.';
      notify(msg);
      return { ok: false, error: msg };
    }
    const isZone = kind === 'zone' || areaType === 'jagtomraade';
    const chosenType: AreaType = areaType ?? (isZone ? 'jagtomraade' : 'mark');
    const typeDef = getAreaTypeDefinition(chosenType, isZone ? 'zone' : 'field');

    const typeCount = fields.filter((f) => {
      if (isJagtPoiField(f)) return false;
      const def = getAreaTypeDefinition(f.areaType, f.kind);
      return def.id === typeDef.id;
    }).length;

    const defaultName =
      customName?.trim() ||
      (isFreeAccount
        ? isZone
          ? `Område${typeCount ? ` ${typeCount + 1}` : ''}`
          : `Vildtager ${typeCount + 1}`
        : typeDef.id === 'jagtomraade'
          ? typeCount === 0
            ? 'Jagtområde'
            : `Jagtområde ${typeCount + 1}`
          : typeDef.id === 'mark'
            ? `Mark ${typeCount + 1}`
            : `${typeDef.label} ${typeCount + 1}`);

    const field: Field = {
      id: id(),
      estateId: estate.id,
      name: defaultName,
      geometry,
      notes: customNotes ?? '',
      kind: isZone ? 'zone' : 'field',
      areaType: chosenType,
      updatedAt: new Date().toISOString(),
      ...(lineLengthMeters ? { lineLengthMeters } : {}),
      ...(lineWidthMeters ? { lineWidthMeters } : {}),
    };
    try {
      let created = false;
      let failureReason: string | null = null;
      setState((s) => {
        try {
          const next = addField(s, plan.id, field);
          created = true;
          return {
            ...next,
            plans: next.plans.map((p) =>
              p.id === plan.id
                ? {
                    ...p,
                    selections: Object.fromEntries(
                      Object.entries(p.selections).filter(([k]) => k !== field.id),
                    ),
                  }
                : p,
            ),
          };
        } catch (err) {
          const msg =
            err instanceof Error
              ? err.message
              : isZone
                ? 'Området kunne ikke føjes til planen.'
                : 'Marken kunne ikke føjes til planen.';
          failureReason = msg;
          notify(msg);
          return s;
        }
      });
      if (created) {
        setSelectedFieldId(field.id);
        if (isFreeAccount && !isZone) setBlendFieldId(field.id);

        // Kobl med integration hvis feltet blev oprettet via 'Plot på kortet' fra Integrationer
        if (typeof window !== 'undefined') {
          try {
            const rawPending = sessionStorage.getItem('plotwise.pendingPoiPlacement');
            if (rawPending) {
              const pending = JSON.parse(rawPending);
              if (pending && pending.integrationId) {
                const currentIntegrations = loadEstateIntegrations(field.estateId);
                let linked = false;
                const updatedCams = currentIntegrations.cameras.map((c) => {
                  if (c.id === pending.integrationId) {
                    linked = true;
                    return { ...c, fieldId: field.id };
                  }
                  return c;
                });
                const updatedTraps = currentIntegrations.traps.map((t) => {
                  if (t.id === pending.integrationId) {
                    linked = true;
                    return { ...t, fieldId: field.id };
                  }
                  return t;
                });
                if (linked) {
                  saveEstateIntegrations(field.estateId, {
                    ...currentIntegrations,
                    cameras: updatedCams,
                    traps: updatedTraps,
                    updatedAt: new Date().toISOString(),
                  });
                }
              }
              sessionStorage.removeItem('plotwise.pendingPoiPlacement');
            }
          } catch {}
        }

        notify(
          customNotes?.startsWith('[POI:')
            ? `${field.name} er placeret på kortet.`
            : isZone
              ? `${field.name} er oprettet som revirgrænse.`
              : `${field.name} er oprettet.`,
        );
        return { ok: true, fieldId: field.id };
      }
      return { ok: false, error: failureReason || 'Feltet kunne ikke tilføjes til planen.' };
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : isZone
            ? 'Området kunne ikke oprettes.'
            : 'Marken kunne ikke oprettes.';
      notify(msg);
      return { ok: false, error: msg };
    }
  }
  function updateField(fieldId: string, patch: Partial<Field>) {
    if (
      activeCustomer.current !== customerWorkspace ||
      !fields.some((field) => field.id === fieldId)
    )
      return;
    if (planReadOnly || !plan) return;
    try {
      setState((s) => {
        try {
          return editField(s, plan.id, fieldId, patch);
        } catch (err) {
          notify(err instanceof Error ? err.message : 'Marken kunne ikke opdateres.');
          return s;
        }
      });
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Fejl ved opdatering af mark.');
    }
  }
  function deleteField(field: Field) {
    const isArea = isNatureOrZoneField(field);
    if (
      planReadOnly ||
      !plan ||
      !confirm(`Slet ${field.name} fra denne plan? Færdige planer bevares.`)
    )
      return;
    const result = removeField(state, plan.id, field);
    setDeleted(result.undo);
    setState(result.state);
    setSelectedFieldId('');

    // Hvis dette felt var koblet til et vildtkamera eller en fældesensor i Integrationer:
    try {
      unlinkFieldFromIntegrations(field.estateId, field.id);
    } catch {}

    notify(
      isArea
        ? `${field.name} er slettet fra den aktuelle plan. Du kan gendanne det.`
        : 'Marken er slettet fra den aktuelle plan. Du kan gendanne den.',
    );
  }
  function restoreField() {
    if (!deleted || planReadOnly) return;
    const wasArea = isNatureOrZoneField(deleted.field);
    try {
      const next = restoreDeletedField(state, deleted);
      setState(next);
      setSelectedFieldId(deleted.field.id);
      setDeleted(null);
      notify(
        wasArea
          ? 'Området og dets tilknytninger er gendannet.'
          : 'Marken og dens tilknytninger er gendannet.',
      );
    } catch (error) {
      setDeleted(null);
      notify(
        error instanceof Error
          ? error.message
          : wasArea
            ? 'Området kunne ikke gendannes.'
            : 'Marken kunne ikke gendannes.',
      );
    }
  }

  function addCustomAreaToCart(productId: string, areaHa: number) {
    const product = products.find((p) => p.id === productId);
    if (!product) return;
    const safeHa = Number.isFinite(areaHa) && areaHa > 0 ? areaHa : 0.5;

    // Sørg for at der findes et revir og en plan
    let targetEstateId = estate?.id || state.selectedEstateId;
    let targetPlanId = state.selectedPlanId;
    const nextState = { ...state };

    if (!targetEstateId || !nextState.estates.some((e) => e.id === targetEstateId)) {
      const newEst = {
        id: id(),
        name: 'Mit Revir',
        address: 'Danmark',
        center: [10.6, 55.4] as [number, number],
        createdAt: new Date().toISOString(),
      };
      nextState.estates = [...nextState.estates, newEst];
      targetEstateId = newEst.id;
      nextState.selectedEstateId = targetEstateId;
    }

    if (
      !targetPlanId ||
      !nextState.plans.some((p) => p.id === targetPlanId && p.estateId === targetEstateId)
    ) {
      const year = new Date().getFullYear();
      const newPl: Plan = {
        id: id(),
        estateId: targetEstateId,
        name: `Plejeplan ${year}`,
        year,
        status: 'draft',
        notes: '',
        selections: {},
        updatedAt: new Date().toISOString(),
      };
      nextState.plans = [...nextState.plans, newPl];
      targetPlanId = newPl.id;
      nextState.selectedPlanId = targetPlanId;
    }

    // Opret en fiktiv/manuel mark med det specificerede areal ved det aktive revirs centrum
    // d = sqrt(safeHa * 10000) i meter. 1 grad lat ca. 111320 m, 1 grad lng ca. 63000 m.
    const sideM = Math.sqrt(safeHa * 10000);
    const dLat = sideM / 111320;
    const dLng = sideM / 63000;
    const activeEstate = nextState.estates.find((e) => e.id === targetEstateId) ?? estate;
    const existingCount = nextState.fields.filter(
      (f) => f.estateId === targetEstateId && !isJagtPoiField(f),
    ).length;
    const offsetIndex = existingCount;
    const centerLng = Number.isFinite(activeEstate?.center?.[0]) ? activeEstate!.center[0] : 10.6;
    const centerLat = Number.isFinite(activeEstate?.center?.[1]) ? activeEstate!.center[1] : 55.4;
    const baseLng = centerLng + (offsetIndex % 10) * 0.002;
    const baseLat = centerLat + Math.floor(offsetIndex / 10) * 0.002;

    const newField: Field = {
      id: id(),
      estateId: targetEstateId,
      name: `Areal ${existingCount + 1} (${safeHa < 0.1 ? safeHa.toFixed(3) : safeHa.toFixed(2)} ha)`,
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [baseLng, baseLat],
            [baseLng + dLng, baseLat],
            [baseLng + dLng, baseLat + dLat],
            [baseLng, baseLat + dLat],
            [baseLng, baseLat],
          ],
        ],
      },
      notes: `Manuelt areal (${safeHa.toFixed(2)} ha) til ${product.name}`,
      updatedAt: new Date().toISOString(),
    };

    nextState.fields = [...nextState.fields, newField];
    nextState.plans = nextState.plans.map((p) =>
      p.id === targetPlanId
        ? {
            ...p,
            selections: { ...p.selections, [newField.id]: productId },
            updatedAt: new Date().toISOString(),
          }
        : p,
    );

    setState(nextState);
    setBasket(true);
    notify(`${product.name} (${safeHa.toFixed(2)} ha) er lagt i din indkøbsliste!`);
  }

  function removeProductFromCart(productId: string) {
    if (planReadOnly || !plan) return;
    const affectedFields = fields.filter((f) => plan.selections[f.id] === productId);
    setState((s) => ({
      ...s,
      plans: s.plans.map((p) =>
        p.id === plan.id
          ? {
              ...p,
              selections: Object.fromEntries(
                Object.entries(p.selections).filter(
                  ([fieldId]) => !affectedFields.some((f) => f.id === fieldId),
                ),
              ),
              updatedAt: new Date().toISOString(),
            }
          : p,
      ),
    }));
    setChecks((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
    const fieldNames = affectedFields.map((f) => f.name).join(', ');
    notify(
      affectedFields.length > 0
        ? `Fjernet fra kurven. ${fieldNames} står nu uden valgt frøblanding.`
        : 'Varen er fjernet fra kurven.',
    );
  }

  const selectedLines = lines.filter((l) => checks[l.product.id] !== false);
  const getPacks = (productId: string, recommended: number) => overrides[productId] ?? recommended;
  const subtotal = selectedLines.reduce(
    (sum, l) => sum + getPacks(l.product.id, l.recommendedPacks) * l.product.priceDkk,
    0,
  );
  async function shopAction(checkout: boolean) {
    if (isDemo && checkout) {
      setShopMessage(
        'Rundtur: din indkøbsliste er beregnet. Ingen ordre oprettes, og ingen varer overføres til webshoppen.',
      );
      return;
    }
    if (isFreeAccount && issues.length) {
      setShopMessage('Vælg frøblanding til alle vildtagre før indkøb beregnes.');
      return;
    }
    const scope = shopScope.current;
    setShopBusy(true);
    setShopMessage(checkout ? 'Gør kurven klar hos Faunamaster…' : 'Henter aktuelle produktdata…');
    try {
      let currentProducts = products;
      // Auto-sync if any selected product is not yet verified or has no variantId
      if (selectedLines.some((line) => !line.product.verified || !line.product.variantId)) {
        currentProducts = await syncProductsFromFaunamaster();
      }

      const items = selectedLines.map((line) => {
        const fresh = currentProducts.find((p) => p.id === line.product.id) ?? line.product;
        return {
          id: fresh.id,
          quantity: getPacks(fresh.id, line.recommendedPacks),
          variantId: fresh.variantId,
          priceDkk: fresh.priceDkk,
          rateKgHa: fresh.rateKgHa,
          packKg: fresh.packKg,
          unit: fresh.unit ?? 'kg',
        };
      });

      const path = checkout
        ? `/api/faunamaster/cart?${new URLSearchParams({ items: JSON.stringify(items) })}`
        : '/api/faunamaster';
      const response = await fetch(path, { signal: AbortSignal.timeout(20000) });
      const result = await response.json();
      if (scope !== shopScope.current) return;
      if (!response.ok) throw new Error(result.error || 'Produktdata kunne ikke hentes.');
      if (checkout) {
        const url = new URL(result.url);
        if (url.origin !== 'https://shop.faunamaster.com' || !url.pathname.startsWith('/cart/'))
          throw new Error('Ugyldigt kurvlink.');

        const activeEstate = state.estates.find((e) => e.id === selectedEstateId);
        const totalEstimatedDkk = items.reduce(
          (sum: number, it: any) => sum + (it.priceDkk || 0) * (it.quantity || 1),
          0,
        );
        void recordFaunamasterClick({
          userId: user?.id,
          userEmail: user?.email,
          userName: state.profile?.name || undefined,
          estateId: activeEstate?.id,
          estateName: activeEstate?.name,
          source: 'cart_checkout',
          targetUrl: url.href,
          items: items.map((it: any) => ({
            name: it.id,
            quantity: it.quantity,
            priceDkk: (it.priceDkk || 0) * (it.quantity || 1),
          })),
          estimatedValueDkk: totalEstimatedDkk,
        });

        window.location.assign(url.href);
      } else {
        const incoming = result.products as Product[];
        const known = new Set([
          'deer',
          'clover',
          'flower',
          'pheasant',
          'winter',
          'cover',
          'brassica',
          'grass',
        ]);
        if (isDemo) {
          const updated = DEFAULT_PRODUCTS.map((p) => {
            const inc = incoming.find((next) => next.id === p.id);
            return inc ? { ...p, ...inc, active: true } : { ...p, active: true };
          });
          setState((previous) => ({
            ...previous,
            products: updated,
          }));
        } else {
          setState((previous) => ({
            ...previous,
            products: [
              ...previous.products.filter((p) => !known.has(p.id)),
              ...previous.products
                .filter((p) => known.has(p.id) && !incoming.some((next) => next.id === p.id))
                .map((p) => ({ ...p, active: false, verified: false })),
              ...incoming,
            ],
          }));
        }
        setOverrides({});
        setShopMessage(
          `Produktdata opdateret. ${result.discountConfigured ? 'Partnerkoden BEINER sendes til webshoppen. Endelig rabat vises hos Faunamaster.' : 'Rabatkode og provision er endnu ikke tilsluttet.'} ${(result.errors ?? []).join(' ')}`,
        );
      }
    } catch (error) {
      if (scope === shopScope.current)
        setShopMessage(error instanceof Error ? error.message : 'Faunamaster er utilgængelig.');
    } finally {
      if (scope === shopScope.current) setShopBusy(false);
    }
  }

  async function syncProductsFromFaunamaster(): Promise<Product[]> {
    const scope = shopScope.current;
    const startGeneration = generation;
    try {
      const response = await fetch('/api/faunamaster', { signal: AbortSignal.timeout(15000) });
      if (!response.ok) return products;
      const result = await response.json();
      if (scope !== shopScope.current || startGeneration !== currentGeneration.current)
        return products;
      if (!Array.isArray(result.products)) return products;
      const incoming = result.products as Product[];
      const known = new Set([
        'deer',
        'clover',
        'flower',
        'pheasant',
        'winter',
        'cover',
        'brassica',
        'grass',
      ]);
      if (isDemo) {
        const updated = DEFAULT_PRODUCTS.map((p) => {
          const inc = incoming.find((next) => next.id === p.id);
          return inc ? { ...p, ...inc, active: true } : { ...p, active: true };
        });
        setState((previous) => ({
          ...previous,
          products: updated,
        }));
        return updated;
      }
      const updated = [
        ...products.filter((p) => !known.has(p.id)),
        ...products
          .filter((p) => known.has(p.id) && !incoming.some((next) => next.id === p.id))
          .map((p) => ({ ...p, active: false, verified: false })),
        ...incoming,
      ];
      setState((previous) => ({
        ...previous,
        products: updated,
      }));
      return updated;
    } catch {
      return products;
    }
  }

  // Auto-sync product data on app load to ensure live stock and prices
  useEffect(() => {
    if (isDemo || user || enteredWorkspace) void syncProductsFromFaunamaster();
  }, [user?.id, enteredWorkspace, isDemo]);

  // Auto-sync product data when shopping list is opened
  useEffect(() => {
    if (!basket) return;
    const hasUnverified = products.some((p) => !p.verified);
    if (hasUnverified) {
      void syncProductsFromFaunamaster();
    }
  }, [basket]);
  const isExample = state.estates.some((e) => e.id === 'example-estate');
  if (!authReady)
    return (
      <div className="empty-state" role={storageError ? 'alert' : 'status'}>
        {storageError ? (
          <>
            <p>{storageError}</p>
            <button className="button primary" onClick={() => window.location.reload()}>
              Prøv igen
            </button>
          </>
        ) : (
          <p>Indlæser arbejdsplads…</p>
        )}
      </div>
    );
  if (!isDemo && !user && !enteredWorkspace && !isPreviewAdmin && !isPreviewPro)
    return (
      <LandingPage
        onLoginSuccess={() => {
          setEnteredWorkspace(true);
        }}
        onStartGuest={() => {
          try {
            localStorage.setItem('plotwise.guest.active', '1');
          } catch {
            // ignore
          }
          setEnteredWorkspace(true);
        }}
        onStartTour={onStartTour}
        initialShowLogin={initialShowLogin}
        focusStartTour={returnedFromTour}
      />
    );

  const requiresMfa = isMfaRequiredForAccount(user, accountType, isAdmin);
  // 2FA-bestået gælder kun for den bruger, der har bestået den, og betroet enhed er bundet til bruger.
  const mfaOk = Boolean(user && (mfaPassedFor === user.id || isDeviceMfaTrusted(user.id)));
  const showMfa = Boolean(user && user.email && requiresMfa && !mfaOk);

  if (showMfa && user?.email) {
    return (
      <TwoFactorAuthModal
        email={user.email}
        userId={user.id}
        onSuccess={() => setMfaPassedFor(user.id)}
        onSignOut={signOut}
      />
    );
  }

  function handleSelectProductForField(fieldId: string, productId: string) {
    setState((s) => {
      const curPlanId = s.selectedPlanId;
      const planList = s.plans;
      if (!planList.length || !planList.some((p) => p.id === curPlanId)) {
        const fallbackPlan: Plan = {
          id: curPlanId || id(),
          estateId: s.selectedEstateId || s.estates[0]?.id || id(),
          name: 'Vildtager ' + new Date().getFullYear(),
          year: new Date().getFullYear(),
          status: 'draft',
          notes: '',
          selections: productId ? { [fieldId]: productId } : {},
          updatedAt: new Date().toISOString(),
        };
        return {
          ...s,
          plans: [fallbackPlan],
          selectedPlanId: fallbackPlan.id,
        };
      }
      return {
        ...s,
        plans: planList.map((p) =>
          p.id === curPlanId
            ? {
                ...p,
                selections: productId
                  ? { ...p.selections, [fieldId]: productId }
                  : Object.fromEntries(
                      Object.entries(p.selections).filter(([key]) => key !== fieldId),
                    ),
                updatedAt: new Date().toISOString(),
              }
            : p,
        ),
      };
    });
  }

  const renderMapComponent = () => (
    <ErrorBoundary fallbackTitle="Kortet kunne ikke indlæses">
      <FieldMap
        isGuest={isGuest}
        isProView={effectiveIsProView}
        isSemiPro={isSemiPro}
        estateDashboardMode={estateDashboardMode}
        simpleMode={isFreeAccount || (isAdvisorWorkspace && estateDashboardMode === 'simple')}
        isPanelCollapsed={isFieldPanelCollapsed}
        onTogglePanel={() => setIsFieldPanelCollapsed((c) => !c)}
        editor={undefined}
        onFullscreenChange={setMapFullscreen}
        readOnly={planReadOnly}
        workspaceScopeKey={`${generation}:${estate?.id ?? ''}:${plan?.id ?? ''}`}
        estate={
          estate || {
            id: '',
            name: 'Nyt revir',
            address: '',
            center: [11.82, 55.4],
            createdAt: now(),
          }
        }
        fields={fields}
        products={products}
        selections={planSelections}
        selectedFieldId={selectedFieldId}
        onSelect={selectField}
        onCenterChange={(_center) => {
          // Intentionally not overwriting estate.center on pan to keep estate anchored to its address
        }}
        onCreate={createField}
        onUpdate={(fieldId, geometry) => updateField(fieldId, { geometry })}
        notify={notify}
        isEditingShape={isEditingShape}
        onToggleEditShape={(editing) => {
          setIsEditingShape((v) => (editing !== undefined ? editing : !v));
        }}
        triggerGpsModal={triggerGpsModal}
        onGpsModalHandled={() => setTriggerGpsModal(false)}
        triggerDraw={drawRequest}
        onDrawTriggered={() => setDrawRequest(null)}
        onDrawingChange={setIsDrawingMap}
        onLayerStateChange={(layers) => {
          setCadastralEnabled(layers.cadastral);
          setMarkblokkeEnabled(layers.markblokke);
        }}
        onNavigate={navigate}
        onOpenUpgradeModal={openUpgradeModal}
        onImportGisFields={batchImportGisFields}
        forceFullscreen={mapFullscreen}
      />
    </ErrorBoundary>
  );

  const renderContentPages = () => (
    <div className="content-page">
      {page === 'clients' && canAccessPro && (
        <ErrorBoundary fallbackTitle="Kundelisten kunne ikke indlæses">
          <ProfessionalClients
            key={`professional-clients-${user?.id ?? 'pro'}`}
            notify={notify}
            onNavigate={navigate}
            onEnterCustomerWorkspace={openCustomer}
            allEstates={state.estates}
          />
        </ErrorBoundary>
      )}
      {page === 'profile' && (
        <AccountPanel
          key={`account-${user?.id ?? 'guest'}`}
          user={demo ? { id: 'demo', email: 'eksempel@plotwise.dk' } : user}
          temporary={Boolean(demo)}
          state={state}
          setState={replaceState}
          notify={notify}
          onSave={saveAccount}
          onLoad={() => reloadAccount(true)}
          cloudConflict={cloudConflict}
        />
      )}
      {administration &&
        (page === 'superadmin' ||
          page === 'admin-overview' ||
          page === 'admin-actions' ||
          page === 'admin-estates' ||
          page === 'admin-seeds' ||
          page === 'admin-map' ||
          page === 'admin-activity' ||
          page === 'admin-email' ||
          page === 'admin-traffic' ||
          page === 'admin-stats') && (
          <ErrorBoundary fallbackTitle="SuperAdmin kunne ikke indlæses">
            <SuperAdminDashboard
              key={`superadmin-${user?.id ?? 'admin'}`}
              state={state}
              setState={replaceState}
              notify={notify}
              onOpenCustomer={openCustomer}
              onRefreshCloud={() => reloadAccount()}
              initialTab={
                page === 'admin-overview'
                  ? 'overview'
                  : page === 'admin-actions'
                    ? 'advisor-actions'
                    : page === 'admin-estates'
                      ? 'estates'
                      : page === 'admin-seeds'
                        ? 'seeds'
                        : page === 'admin-map'
                          ? 'map'
                          : page === 'admin-email'
                            ? 'email'
                            : page === 'admin-activity'
                              ? 'activity'
                              : page === 'admin-traffic' || page === 'admin-stats'
                                ? 'traffic'
                                : 'customers'
              }
              onSelectTab={(tab) => {
                const pageMap: Record<string, Page> = {
                  overview: 'admin-overview',
                  'advisor-actions': 'admin-actions',
                  customers: 'superadmin',
                  estates: 'admin-estates',
                  seeds: 'admin-seeds',
                  map: 'admin-map',
                  activity: 'admin-activity',
                  email: 'admin-email',
                  traffic: 'admin-stats',
                };
                navigate(pageMap[tab] || 'superadmin');
              }}
            />
          </ErrorBoundary>
        )}
      {page === 'forest' ? (
        <ErrorBoundary fallbackTitle="Skovrejsning kunne ikke indlæses">
          <ForestPanel state={state} />
        </ErrorBoundary>
      ) : page === 'raccoon-map' ? (
        <ErrorBoundary fallbackTitle="Mårhundekortet kunne ikke indlæses">
          <RaccoonMapPage notify={notify} isAdmin={isAdmin} />
        </ErrorBoundary>
      ) : page === 'catalog' ? (
        <ErrorBoundary fallbackTitle="Frøkataloget kunne ikke indlæses">
          <CatalogPanel
            key={`catalog-${user?.id ?? 'guest'}-${customerWorkspace?.userId ?? 'admin'}`}
            state={state}
            setState={replaceState}
            notify={notify}
            allowAdministration={Boolean(administration && !demo)}
            selectedFieldId={selectedFieldId}
            onNavigate={navigate}
            onAddToCart={(productId, areaHa) => {
              addCustomAreaToCart(productId, areaHa);
            }}
          />
        </ErrorBoundary>
      ) : page === 'tasks' && !effectiveIsProView ? (
        <Suspense fallback={<div className="loading" />}>
          <TasksPage state={state} setState={replaceState} notify={notify} />
        </Suspense>
      ) : page !== 'superadmin' &&
        page !== 'admin-seeds' &&
        page !== 'admin-map' &&
        page !== 'admin-activity' &&
        page !== 'admin-email' &&
        page !== 'admin-traffic' &&
        page !== 'admin-stats' &&
        page !== 'clients' ? (
        <WorkspacePages
          key={`workspace-${user?.id ?? 'guest'}-${customerWorkspace?.userId ?? 'self'}`}
          page={page}
          state={
            customerWorkspace
              ? {
                  ...visibleState,
                  profile: { ...visibleState.profile, name: customerWorkspace.name },
                }
              : visibleState
          }
          setState={customerWorkspace ? replaceCustomerState : replaceState}
          notify={notify}
          onNavigate={navigate}
          openNewEstate={openNewEstateOnOverview}
          onClearOpenNewEstate={() => setOpenNewEstateOnOverview(false)}
          onRefreshCloud={() => reloadAccount()}
          allowEstateCreation={allowEstateCreation}
          temporaryFiles={demo ? demoFiles.current : undefined}
          isProView={effectiveIsProView}
          showAdvanced={effectiveShowAdvanced}
          clients={proClients}
          onEnterCustomerWorkspace={openCustomer}
          estateDashboardMode={estateDashboardMode}
          onSelectDashboardMode={handleSelectDashboardMode}
          showModeSwitcher={isAdmin || isProfessional}
          onOpenNewEstate={() => setIsCreateEstateModalOpen(true)}
          onSelectEstate={(id) => chooseEstate(id)}
          showGuideBanner={!guideDismissed && !tourActive && !demo}
          onStartGuide={() => handleStartTour(guideUserTier)}
          onDismissGuide={handleDismissGuide}
          guideUserTier={guideUserTier}
        />
      ) : null}
      {page === 'profile' && isAdmin && (
        <section className="panel">
          <h3>Skærmmåling (kun admin)</h3>
          <p className="muted" style={{ marginBottom: 12 }}>
            Viser skærmens mål og safe-area i et lille panel, så layout på iPhone kan fejlsøges.
          </p>
          <button
            type="button"
            className="secondary-button"
            style={{ minHeight: 44 }}
            onClick={() => toggleViewportDebug()}
          >
            {isViewportDebugOn() ? 'Skjul skærmmåling' : 'Vis skærmmåling'}
          </button>
        </section>
      )}
      {page === 'profile' && !administration && (
        <section className="panel pro-view-profile-section">
          {isProfessional && (
            <div className="pro-mode-profile-block" style={{ marginBottom: 16 }}>
              <h3>Visningstilstand for professionel konto</h3>
              <p className="muted" style={{ marginBottom: 12 }}>
                Vælg om du vil se alle professionelle værktøjer fremhævet, eller skifte til den
                enkle arbejdsplads til din egen jagt.
              </p>
              <div className="pro-mode-radio-group">
                <label className="pro-mode-radio-label">
                  <input
                    type="radio"
                    name="proMode"
                    value="professional"
                    checked={proMode === 'professional'}
                    onChange={() => {
                      setProMode('professional');
                      try {
                        localStorage.setItem('plotwise.proMode', 'professional');
                      } catch {}
                      notify('Skiftet til professionel visning.');
                    }}
                  />
                  <div>
                    <strong>Professionel visning (fremhævet)</strong>
                    <span
                      className="muted"
                      style={{ display: 'block', fontSize: 12, marginTop: 2 }}
                    >
                      Viser kundestyring, GPS-værktøjer, planhistorik og fuld eksport.
                    </span>
                  </div>
                </label>
                <label className="pro-mode-radio-label">
                  <input
                    type="radio"
                    name="proMode"
                    value="simple"
                    checked={proMode === 'simple'}
                    onChange={() => {
                      setProMode('simple');
                      try {
                        localStorage.setItem('plotwise.proMode', 'simple');
                      } catch {}
                      notify('Skiftet til simpel visning til din egen jagt.');
                    }}
                  />
                  <div>
                    <strong>Simpel visning (egen jagt)</strong>
                    <span
                      className="muted"
                      style={{ display: 'block', fontSize: 12, marginTop: 2 }}
                    >
                      Friktionsfri arbejdsplads med fokus på adresse, optegning og frøblandinger.
                    </span>
                  </div>
                </label>
              </div>
            </div>
          )}
          {/* En almindelig konto skal slet ikke have denne indstilling. Den er for
              en professionel der midlertidigt staar i den simple visning og vil se
              hele arbejdspladsen igen. Skellet ligger paa kontotypen. */}
          {estateDashboardMode !== 'simple' &&
            (isProfessionalAccount({ accountType, isAdmin, proMode }) ||
              (!user && enteredWorkspace) ||
              Boolean(demo)) &&
            !administration &&
            false && (
              <>
                <label>
                  <input
                    type="checkbox"
                    checked={advanced}
                    onChange={(event) => setAdvanced(event.target.checked)}
                  />{' '}
                  Vis sæsoner, aktiviteter og dokumenter
                </label>
                <p className="muted">
                  Udvidet planlægning vises i arbejdspladsen. Administrationen har sit eget
                  overblik.
                </p>
              </>
            )}
        </section>
      )}
    </div>
  );

  const syncStatusLabel =
    user && configured
      ? cloudError
        ? cloudConflict
          ? 'Versioner skal sammenholdes'
          : 'Gemt lokalt · prøver igen'
        : cloudBusy
          ? 'Gemmer i kontoen…'
          : cloudDirty
            ? `${saveLabel} · afventer konto`
            : cloudGeneration === generation
              ? 'Gemt i kontoen'
              : saveLabel
      : saveLabel;
  const syncBanners = (
    <>
      {storageError && (
        <div className="error-banner" role="alert">
          {storageError} Eksportér dine planer fra profilen. Eksisterende beskadigede data
          overskrives ikke.
        </div>
      )}
      {cloudError && user && !demo && (
        <div
          className="error-banner"
          role="alert"
          style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}
        >
          <div>
            <span>{cloudError}</span>{' '}
            {!cloudConflict && !storageError && <span>Vi forsøger automatisk igen.</span>}
          </div>
          {cloudConflict && (
            <div
              className="inline-actions"
              style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}
            >
              <button
                className="button primary"
                onClick={() =>
                  void reloadAccount(true)
                    .then(() => notify('Serverens version er indlæst.'))
                    .catch((err) =>
                      notify(
                        err instanceof Error
                          ? err.message
                          : 'Serverens version kunne ikke indlæses.',
                      ),
                    )
                }
              >
                Prøv at indlæse serverens version igen
              </button>
              <button
                className="button secondary"
                onClick={() => downloadState(fullState, 'plotwise-lokale-aendringer')}
              >
                Download denne enheds version
              </button>
              <button
                className="button secondary"
                onClick={() =>
                  downloadState(cloudConflict.remote, 'plotwise-serverversion-ved-sammenligning')
                }
              >
                Download serverens version
              </button>
            </div>
          )}
          <div className="inline-actions" style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="button secondary" onClick={() => navigate('profile')}>
              Åbn konto og sikkerhedskopi
            </button>
            {!cloudDirty && (
              <button className="button secondary" onClick={() => void reloadAccount()}>
                Prøv igen
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );

  const appContent = (
    <Suspense fallback={<div className="empty-state">Indlæser Plotwise…</div>}>
      <div
        className={`app-shell ${mapFullscreen ? 'map-fullscreen-active' : ''} ${effectiveIsProView ? 'view-professional' : 'view-standard'} ${advanced ? 'view-advanced-tools' : ''} ${tourActive ? 'user-guide-active' : ''} ${tourActive && tourCollapsed ? 'guide-collapsed' : ''}`}
      >
        <aside inert={basket} className={`sidebar ${mobileNav ? 'is-open' : ''}`}>
          <button
            className="brand"
            onClick={() =>
              // Logoet er "hjem". For en professionel var det kundelisten — samme fejl som
              // ved login: en raadgiver blev sendt til kunderne i stedet for sit eget kort.
              // "Mine kunder" er et aktivt valg i menuen. Administrationen har sit eget
              // overblik.
              navigate(administration ? 'superadmin' : 'overview')
            }
            aria-label="Plotwise overblik"
          >
            <span className="brand-mark">
              <Leaf size={23} />
            </span>
            <span>
              plotwise<span className="brand-period">.</span>
            </span>
          </button>
          <p className="sidebar-caption">DIN JORD. DIN PLAN.</p>
          <div className="nav-label">
            {customerWorkspace
              ? 'KUNDENS ARBEJDSPLADS'
              : isAdmin
                ? adminWorkspaceMode === 'admin'
                  ? 'ADMINISTRATION'
                  : estateDashboardMode === 'simple'
                    ? 'ALMINDELIG (MIT REVIR)'
                    : estateDashboardMode === 'semipro'
                      ? 'SEMIPRO (MIT REVIR)'
                      : 'PROFESSIONEL (MIT REVIR)'
                : isProfessional
                  ? proMode === 'professional'
                    ? 'PROFESSIONEL'
                    : 'MIN JAGT (SIMPEL)'
                  : 'ARBEJDSPLADS'}
          </div>
          {isAdmin && !customerWorkspace && (
            <div className="admin-workspace-switcher">
              <div className="admin-workspace-track" role="tablist" aria-label="Arbejdsområde">
                <button
                  type="button"
                  role="tab"
                  aria-selected={adminWorkspaceMode === 'admin'}
                  className={`admin-workspace-btn ${adminWorkspaceMode === 'admin' ? 'active' : ''}`}
                  onClick={() => {
                    setAdminWorkspaceMode('admin');
                    try {
                      localStorage.setItem('plotwise.adminWorkspaceMode', 'admin');
                    } catch {}
                    navigate('superadmin');
                  }}
                  title="Gå til administration, kunder og globale overblik"
                >
                  <Shield size={13} />
                  <span>Admin</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={adminWorkspaceMode === 'advisor'}
                  className={`admin-workspace-btn is-advisor ${adminWorkspaceMode === 'advisor' ? 'active' : ''}`}
                  onClick={() => {
                    setAdminWorkspaceMode('advisor');
                    try {
                      localStorage.setItem('plotwise.adminWorkspaceMode', 'advisor');
                    } catch {}
                    navigate('overview');
                  }}
                  title="Gå til dit eget revir og profilvisning"
                >
                  <Map size={13} />
                  <span>Mit revir</span>
                </button>
              </div>

              {adminWorkspaceMode === 'advisor' && (
                <div
                  className="advisor-tier-track"
                  role="tablist"
                  aria-label="Vælg profilvisning for Mit revir"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={estateDashboardMode === 'simple'}
                    className={`advisor-tier-btn ${estateDashboardMode === 'simple' ? 'active' : ''}`}
                    onClick={() => handleSelectDashboardMode('simple')}
                    title="Vis 1:1 som almindelig bruger"
                  >
                    <span>Almindelig</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={estateDashboardMode === 'semipro'}
                    className={`advisor-tier-btn ${estateDashboardMode === 'semipro' ? 'active' : ''}`}
                    onClick={() => handleSelectDashboardMode('semipro')}
                    title="Vis 1:1 som semiprofessionel bruger"
                  >
                    <span>Semi-pro</span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={estateDashboardMode === 'pro'}
                    className={`advisor-tier-btn ${estateDashboardMode === 'pro' ? 'active' : ''}`}
                    onClick={() => handleSelectDashboardMode('pro')}
                    title="Vis 1:1 som fuld professionel rådgiver"
                  >
                    <span>Professionel</span>
                  </button>
                </div>
              )}
            </div>
          )}
          {isProfessional && !administration && !customerWorkspace && !isAdmin && (
            <div className="pro-view-switcher">
              <button
                type="button"
                className={`pro-view-toggle-btn ${proMode === 'simple' ? 'is-simple' : 'is-pro'}`}
                onClick={() => {
                  const nextMode = proMode === 'professional' ? 'simple' : 'professional';
                  setProMode(nextMode);
                  try {
                    localStorage.setItem('plotwise.proMode', nextMode);
                  } catch {}
                  notify(
                    nextMode === 'simple'
                      ? 'Skiftet til simpel visning for din egen jagt.'
                      : 'Skiftet til fuld professionel visning.',
                  );
                }}
                title={
                  proMode === 'professional'
                    ? 'Skift til simpel visning for din egen jagt'
                    : 'Skift tilbage til fuld professionel visning'
                }
              >
                {proMode === 'professional' ? (
                  <>
                    <Eye size={14} />
                    <span>Simpel visning (egen jagt)</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    <span>Professionel visning</span>
                  </>
                )}
              </button>
            </div>
          )}
          <nav id="main-navigation" aria-label="Hovedmenu">
            {administration ? (
              <>
                <button
                  className={`nav-item ${
                    page === 'superadmin' ||
                    page === 'admin-overview' ||
                    page === 'admin-actions' ||
                    page === 'admin-estates'
                      ? 'active'
                      : ''
                  }`}
                  onClick={() => navigate('superadmin')}
                  aria-label="Kunder & Revirer"
                >
                  <Users size={19} />
                  <span>Kunder &amp; Revirer</span>
                  {(page === 'superadmin' ||
                    page === 'admin-overview' ||
                    page === 'admin-actions' ||
                    page === 'admin-estates') && <span className="nav-active-dot" />}
                </button>
                <button
                  className={`nav-item ${page === 'admin-map' ? 'active' : ''}`}
                  onClick={() => navigate('admin-map')}
                  aria-label="Danmarkskort"
                >
                  <MapPin size={19} />
                  <span>Danmarkskort</span>
                  {page === 'admin-map' && <span className="nav-active-dot" />}
                </button>
                <button
                  className={`nav-item ${page === 'admin-seeds' ? 'active' : ''}`}
                  onClick={() => navigate('admin-seeds')}
                  aria-label="Frøcenter"
                >
                  <Sprout size={19} />
                  <span>Frøcenter</span>
                  {page === 'admin-seeds' && <span className="nav-active-dot" />}
                </button>
                <button
                  className={`nav-item ${
                    page === 'admin-activity' || page === 'admin-stats' || page === 'admin-traffic'
                      ? 'active'
                      : ''
                  }`}
                  onClick={() => navigate('admin-activity')}
                  aria-label="Aktivitet & Log"
                >
                  <Activity size={19} />
                  <span>Aktivitet &amp; Log</span>
                  {(page === 'admin-activity' ||
                    page === 'admin-stats' ||
                    page === 'admin-traffic') && <span className="nav-active-dot" />}
                </button>
                <button
                  className={`nav-item ${page === 'admin-email' ? 'active' : ''}`}
                  onClick={() => navigate('admin-email')}
                  aria-label="E-mailliste"
                >
                  <Mail size={19} />
                  <span>E-mailliste</span>
                  {page === 'admin-email' && <span className="nav-active-dot" />}
                </button>

                <div
                  className="nav-subheading"
                  style={{
                    marginTop: 14,
                    paddingTop: 10,
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      color: 'rgba(255, 255, 255, 0.68)',
                      textTransform: 'uppercase',
                      padding: '0 12px',
                      display: 'block',
                      marginBottom: 6,
                    }}
                  >
                    Min Arbejdsplads
                  </span>
                  <button
                    className={`nav-item ${page === 'overview' ? 'active' : ''}`}
                    onClick={() => {
                      setAdminWorkspaceMode('advisor');
                      try {
                        localStorage.setItem('plotwise.adminWorkspaceMode', 'advisor');
                        localStorage.setItem('plotwise.estateDashboardMode', 'pro');
                      } catch {}
                      navigate('overview');
                    }}
                  >
                    <LayoutDashboard size={19} />
                    <span>Revir Dashboard</span>
                  </button>
                  <button
                    className="nav-item"
                    onClick={() => {
                      setAdminWorkspaceMode('advisor');
                      try {
                        localStorage.setItem('plotwise.adminWorkspaceMode', 'advisor');
                      } catch {}
                      navigate('map');
                    }}
                  >
                    <Map size={19} />
                    <span>Mine marker</span>
                  </button>
                  <button
                    className="nav-item"
                    onClick={() => {
                      setAdminWorkspaceMode('advisor');
                      try {
                        localStorage.setItem('plotwise.adminWorkspaceMode', 'advisor');
                      } catch {}
                      navigate('catalog');
                    }}
                  >
                    <Leaf size={19} />
                    <span>Frøblandinger</span>
                  </button>
                  <button
                    className="nav-item"
                    onClick={() => {
                      setAdminWorkspaceMode('advisor');
                      try {
                        localStorage.setItem('plotwise.adminWorkspaceMode', 'advisor');
                      } catch {}
                      navigate('plans');
                    }}
                  >
                    <FileText size={19} />
                    <span>Plejeplaner</span>
                  </button>
                  <button
                    className="nav-item"
                    onClick={() => {
                      setAdminWorkspaceMode('advisor');
                      try {
                        localStorage.setItem('plotwise.adminWorkspaceMode', 'advisor');
                      } catch {}
                      navigate('biotope-plan');
                    }}
                  >
                    <LandPlot size={19} />
                    <span>Biotopplan</span>
                  </button>
                  <button
                    className="nav-item"
                    onClick={() => {
                      setAdminWorkspaceMode('advisor');
                      try {
                        localStorage.setItem('plotwise.adminWorkspaceMode', 'advisor');
                      } catch {}
                      navigate('seeding');
                    }}
                  >
                    <ThermometerSun size={19} />
                    <span>Såningsgennemgang</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  data-guide="nav-overview"
                  className={`nav-item ${page === 'overview' ? 'active' : ''}`}
                  onClick={() => navigate('overview')}
                >
                  <LayoutDashboard size={19} />
                  <span>Overblik</span>
                  {page === 'overview' && <span className="nav-active-dot" />}
                </button>
                {canAccessSemiPro && (
                  <button
                    data-guide="nav-clients"
                    className={`nav-item ${page === 'clients' ? 'active' : ''} ${!canAccessPro ? 'nav-item-locked' : ''}`}
                    onClick={() => {
                      if (canAccessPro) {
                        navigate('clients');
                      } else {
                        openUpgradeModal(
                          'Mine kunder',
                          'pro',
                          'Få samlet kundestyring, adgang til kunders revirer og professionelle rådgivningsrapporter.',
                        );
                      }
                    }}
                    title={
                      !canAccessPro
                        ? 'Mine kunder kræver Professionel adgang · Klik for at opgradere'
                        : undefined
                    }
                  >
                    <Users size={19} />
                    <span>Mine kunder</span>
                    {!canAccessPro && (
                      <span className="nav-item-badge-wrap">
                        <Lock size={12} className="nav-item-lock" />
                        <span className="nav-tier-badge pro">PRO</span>
                      </span>
                    )}
                    {page === 'clients' && <span className="nav-active-dot" />}
                  </button>
                )}
                <button
                  data-guide="nav-map"
                  className={`nav-item ${page === 'map' ? 'active' : ''}`}
                  onClick={() => navigate('map')}
                >
                  <Map size={19} />
                  <span>Mine marker</span>
                  {page === 'map' && <span className="nav-active-dot" />}
                </button>
                <button
                  data-guide="nav-catalog"
                  className={`nav-item ${page === 'catalog' ? 'active' : ''}`}
                  onClick={() => navigate('catalog')}
                >
                  <Leaf size={19} />
                  <span>Frøblandinger</span>
                  {page === 'catalog' && <span className="nav-active-dot" />}
                </button>
                <button
                  data-guide="nav-doubts"
                  className="nav-item"
                  onClick={() => {
                    setShowAdvisorModal(true);
                    setMobileNav(false);
                  }}
                  style={{ color: '#a3c27e' }}
                  title="Få hjælp til at vælge de rigtige frøblandinger"
                >
                  <HelpCircle size={19} />
                  <span>Er du i tvivl?</span>
                </button>
                {canAccessSemiPro &&
                  [
                    {
                      id: 'plans' as Page,
                      name: 'Plejeplaner',
                      icon: Sprout,
                      tier: 'semipro' as const,
                      desc: 'Opret flerårige plejeplaner, overvåg markudvikling og planlæg udsåning og pleje for hele reviret.',
                    },
                    {
                      id: 'biotope-plan' as Page,
                      name: 'Biotopplan',
                      icon: LandPlot,
                      tier: 'pro' as const,
                      desc: 'Vejledende biotopplanlægger, kvoteregner og pointopgørelse jf. Bekendtgørelsen § 14.',
                    },
                    {
                      id: 'seeding' as Page,
                      name: 'Såningsgennemgang',
                      icon: ThermometerSun,
                      tier: 'semipro' as const,
                      desc: 'Få adgang til live jordtemperatur (0-7 cm såbed), 14-dages mikroklima prognoser og præcis så-timing.',
                    },
                  ].map(({ id: next, name, icon: Icon, tier, desc }) => {
                    const isLocked = tier === 'pro' ? !canAccessPro : !canAccessSemiPro;
                    const isActive = page === next || (next === 'activities' && page === 'tasks');
                    const badgeClass = tier === 'pro' ? 'pro' : 'semipro';
                    const badgeText = tier === 'pro' ? 'PRO' : 'SEMI-PRO';
                    return (
                      <button
                        key={next}
                        data-guide={`nav-${next}`}
                        className={`nav-item ${isActive ? 'active' : ''} ${isLocked ? 'nav-item-locked' : ''}`}
                        onClick={() => {
                          if (isLocked) {
                            openUpgradeModal(name, tier, desc);
                          } else {
                            navigate(next);
                          }
                        }}
                        title={
                          isLocked
                            ? `${name} kræver ${tier === 'pro' ? 'Professionel' : 'Semi-pro'} adgang · Klik for at opgradere`
                            : undefined
                        }
                      >
                        <Icon size={19} />
                        <span>{name}</span>
                        {isLocked && (
                          <span className="nav-item-badge-wrap">
                            <Lock size={12} className="nav-item-lock" />
                            <span className={`nav-tier-badge ${badgeClass}`}>{badgeText}</span>
                          </span>
                        )}
                        {!isLocked && isActive && <span className="nav-active-dot" />}
                      </button>
                    );
                  })}
              </>
            )}
            {!customerWorkspace &&
              estateDashboardMode !== 'semipro' &&
              estateDashboardMode !== 'pro' && (
                <button
                  data-guide="nav-advisor"
                  className={`nav-item ${page === 'advisor' ? 'active' : ''}`}
                  onClick={() => navigate('advisor')}
                >
                  <Users size={19} />
                  <span>Rådgivning</span>
                </button>
              )}
            <button
              className={`nav-item ${page === 'raccoon-map' ? 'active' : ''}`}
              onClick={() => {
                navigate('raccoon-map');
                setMobileNav(false);
              }}
            >
              <Crosshair size={19} />
              <span>Mårhundekort</span>
              {page === 'raccoon-map' && <span className="nav-active-dot" />}
            </button>
          </nav>
          <div className="sidebar-bottom">
            <button
              data-guide="nav-guide"
              className={`nav-item guide-nav-item ${guideModalOpen || tourActive ? 'active' : ''}`}
              onClick={() => {
                setGuideModalOpen(true);
                setMobileNav(false);
              }}
              style={{ color: '#a3c27e' }}
              title="Start brugerguide & overblik over funktioner"
            >
              <Compass size={19} />
              <span>Guide &amp; Kom godt i gang</span>
            </button>
            <button
              data-guide="nav-profile"
              className={`nav-item ${page === 'profile' ? 'active' : ''}`}
              onClick={() => navigate('profile')}
            >
              <UserRound size={19} />
              {customerWorkspace ? 'Administratorkonto' : 'Konto & profil'}
            </button>
            {/* Opdater app: Altid synlig med lynhurtig 1-tap opdatering og seneste tidspunkt */}
            <button
              className="nav-item pwa-nav-item app-update-nav-item"
              onClick={() => {
                notify('Opdaterer Plotwise app og rydder browser-cache…');
                void applyAppUpdate();
                setMobileNav(false);
              }}
              style={{ color: hasAppUpdate ? '#4ade80' : '#e2e8f0' }}
              title={
                lastUpdatedLabel
                  ? `Opdater Plotwise app (${lastUpdatedLabel})`
                  : 'Opdater Plotwise app til seneste version'
              }
            >
              <RotateCw
                size={19}
                className={isCheckingAppUpdate || isUpdatingApp ? 'animate-spin' : ''}
              />
              <div className="app-update-info">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                  <span>{hasAppUpdate ? 'Opdater app (Ny)' : 'Opdater app'}</span>
                  {hasAppUpdate && (
                    <span
                      style={{
                        fontSize: '0.66rem',
                        fontWeight: 800,
                        background: '#16a34a',
                        color: '#ffffff',
                        padding: '2px 6px',
                        borderRadius: 10,
                        textTransform: 'uppercase',
                      }}
                    >
                      Ny
                    </span>
                  )}
                </div>
                {lastUpdatedLabel && (
                  <span className="app-update-timestamp">{lastUpdatedLabel}</span>
                )}
              </div>
            </button>

            {!isRunningInApp && !PWA_INSTALL_SUSPENDED && (
              <button
                className="nav-item pwa-nav-item"
                onClick={() => {
                  setPwaSheetOpen(true);
                  setMobileNav(false);
                }}
                style={{ color: '#4ade80' }}
                title="Føj Plotwise til din hjemmeskærm som app"
              >
                <Smartphone size={19} />
                <span>Installer som app</span>
              </button>
            )}

            <div className="partner">
              <span>FRØ & VILDTAGRE FRA</span>
              <a
                href="https://shop.faunamaster.com/discount/BEINER"
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  const est = state.estates.find((e) => e.id === selectedEstateId);
                  void recordFaunamasterClick({
                    source: 'shop_link',
                    targetUrl: 'https://shop.faunamaster.com/discount/BEINER',
                    userId: user?.id,
                    userEmail: user?.email,
                    userName: state.profile?.name || undefined,
                    estateId: est?.id,
                    estateName: est?.name,
                  });
                }}
              >
                FAUNAMASTER <ArrowUpRight size={14} />
              </a>
            </div>
            <button className="user-chip" onClick={() => navigate('profile')}>
              <span className="avatar">{state.profile.name.trim().slice(0, 1) || 'P'}</span>
              <span>
                <strong>
                  {state.profile.name ||
                    (demo
                      ? 'Eksempelbruger'
                      : configured && !user
                        ? 'Opret profil / Log ind'
                        : 'Din arbejdsplads')}
                </strong>
                <small>{demo ? 'Eksempelkonto' : user ? 'Din konto' : 'Lokale planer'}</small>
              </span>
              <ChevronDown size={14} />
            </button>
          </div>
        </aside>
        {mobileNav && (
          <button className="nav-scrim" aria-label="Luk menu" onClick={() => setMobileNav(false)} />
        )}
        {isMobile ? (
          <MobileAppShell
            isGuest={isGuest}
            onOpenSavePlan={() => setShowSaveGuestModal(true)}
            page={page}
            onNavigate={navigate}
            estate={estate}
            estates={visibleState.estates}
            selectedEstateId={visibleState.selectedEstateId}
            onSelectEstate={chooseEstate}
            customerWorkspace={customerWorkspace}
            onLeaveCustomer={leaveCustomer}
            isAdmin={Boolean(isAdmin || isPreviewAdmin)}
            isSemiPro={isSemiPro}
            isProView={effectiveIsProView}
            fields={fields}
            products={products}
            planSelections={planSelections}
            onSelectProduct={handleSelectProductForField}
            selectedFieldId={selectedFieldId}
            onSelectField={selectField}
            onUpdateField={(fieldId, updates) => updateField(fieldId, updates)}
            onDeleteField={(fieldId) => {
              const target = fields.find((f) => f.id === fieldId);
              if (target) deleteField(target);
            }}
            onOpenCart={() => setBasket(true)}
            cartCount={lines.length}
            onOpenMenu={() => setMobileNav(true)}
            menuOpen={mobileNav}
            triggerGpsModal={triggerGpsModal}
            onGpsModalHandled={() => setTriggerGpsModal(false)}
            triggerDraw={drawRequest}
            onDrawTriggered={() => setDrawRequest(null)}
            onTriggerDraw={(kind) => setDrawRequest(kind === 'zone' ? 'zone' : 'field')}
            onTriggerGps={() => setTriggerGpsModal(true)}
            notify={notify}
            onToggleEditShape={(editing) => setIsEditingShape(editing ?? true)}
            planReadOnly={planReadOnly}
            estateDashboardMode={estateDashboardMode}
            onSelectDashboardMode={handleSelectDashboardMode}
            allowEstateCreation={allowEstateCreation}
            onCreateEstate={() => setIsCreateEstateModalOpen(true)}
            onOpenPwaInstall={() => setPwaSheetOpen(true)}
            hasAppUpdate={hasAppUpdate}
            onApplyAppUpdate={() => {
              notify('Opdaterer Plotwise app og rydder browser-cache…');
              void applyAppUpdate();
            }}
            lastUpdatedLabel={lastUpdatedLabel}
            deletedFieldName={planReadOnly ? undefined : deleted?.field.name}
            onRestoreDeleted={restoreField}
            syncStatus={syncStatusLabel}
            syncBanners={syncBanners}
            renderMap={renderMapComponent}
            renderPageContent={renderContentPages}
          />
        ) : (
          <main inert={basket || mobileNav} className="main-workspace">
            <header className="topbar">
              <button
                className="mobile-menu icon-button"
                aria-label="Åbn menu"
                aria-expanded={mobileNav}
                aria-controls="main-navigation"
                onClick={() => setMobileNav(!mobileNav)}
              >
                <Menu size={23} />
              </button>
              {customerWorkspace ? (
                <div
                  className="breadcrumb customer-scoped-breadcrumb"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                >
                  <button
                    type="button"
                    onClick={leaveCustomer}
                    className="breadcrumb-link"
                    aria-label="Gå tilbage til Mine kunder"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      color: '#2d6a4f',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                    }}
                  >
                    Mine kunder
                  </button>
                  <span className="breadcrumb-separator" style={{ color: '#999' }}>
                    /
                  </span>
                  {page === 'customer-hub' ? (
                    <strong
                      className="breadcrumb-current"
                      style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a2b21' }}
                    >
                      {customerWorkspace.name}
                    </strong>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => navigate('customer-hub')}
                        className="breadcrumb-link"
                        aria-label={`Tilbage til ${customerWorkspace.name}s revirer`}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                          color: '#2d6a4f',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                        }}
                      >
                        {customerWorkspace.name}
                      </button>
                      <span className="breadcrumb-separator" style={{ color: '#999' }}>
                        /
                      </span>
                      <strong
                        className="breadcrumb-current"
                        style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a2b21' }}
                      >
                        {estate?.name || 'Revir'}
                      </strong>
                    </>
                  )}
                </div>
              ) : (
                <div className="breadcrumb">
                  <span className="breadcrumb-category">
                    {administration
                      ? 'Administration'
                      : isAdvisorWorkspace
                        ? estateDashboardMode === 'simple'
                          ? 'Almindelig'
                          : estateDashboardMode === 'semipro'
                            ? 'Semiprofessionel'
                            : 'Professionel'
                        : canAccessPro && page === 'clients'
                          ? 'Professionel'
                          : 'Arbejdsplads'}
                  </span>{' '}
                  <span className="breadcrumb-separator">/</span>{' '}
                  <strong className="breadcrumb-current">
                    {effectiveIsProView && (page === 'activities' || page === 'tasks')
                      ? 'Aktiviteter & Opgaver'
                      : (nav.find((n) => n.id === page)?.name ??
                        (
                          {
                            clients: 'Mine kunder',
                            'biotope-plan': 'Biotopplan',
                            profile: 'Konto & profil',
                            advisor: 'Rådgivning',
                            catalog: 'Frøblandinger',
                            superadmin: 'Kunder & Revirer',
                            'admin-seeds': 'Frø- & Sæsonbehov',
                            'admin-map': 'Revirkort (Danmark)',
                            'admin-activity': 'Aktivitetsfeed & Log',
                            'admin-email': 'E-mailliste',
                            'raccoon-map': 'Mårhundekort',
                            'admin-traffic': 'Besøgsstatistik & Trafik',
                            'admin-stats': 'Besøgsstatistik & Trafik',
                          } as Record<string, string>
                        )[page])}
                  </strong>
                </div>
              )}
              <div className="topbar-actions">
                <span className={`save-indicator ${storageError ? 'error' : ''}`}>
                  <span />
                  {syncStatusLabel}
                </span>
                {isGuest && fields.length > 0 && (
                  <button
                    type="button"
                    className="button primary"
                    onClick={() => setShowSaveGuestModal(true)}
                    style={{
                      background: '#16a34a',
                      borderColor: '#15803d',
                      color: '#ffffff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    Gem min plan
                  </button>
                )}
                {configured && !user && !demo && (!isGuest || fields.length === 0) && (
                  <button className="button secondary" onClick={() => navigate('profile')}>
                    Log ind
                  </button>
                )}
                {!administration && (
                  <button
                    className="button top-order"
                    aria-label="Åbn indkøbsliste"
                    onClick={() => setBasket(true)}
                  >
                    <ShoppingBag size={17} />
                    <span>Indkøbsliste</span>
                    {lines.length > 0 && (
                      <span className="count" data-count={lines.length} aria-hidden="true" />
                    )}
                  </button>
                )}
              </div>
            </header>
            {customerWorkspace && page !== 'customer-hub' && (
              <section className="customer-workspace-banner" aria-label="Kundens arbejdsplads">
                <div>
                  <strong>{customerWorkspace.name}</strong>
                  <span>
                    {isAdmin
                      ? `Du arbejder som administrator · ${customerWorkspace.email}`
                      : `Du arbejder i ${customerWorkspace.name}s arbejdsplads · ${customerWorkspace.email}`}
                  </span>
                </div>
                {visibleState.estates.length > 1 && (
                  <div
                    className="customer-workspace-estate-picker"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.45rem',
                      background: 'rgba(255, 255, 255, 0.7)',
                      padding: '0.25rem 0.6rem',
                      borderRadius: '6px',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <label
                      htmlFor="customer-workspace-estate-select"
                      style={{ fontSize: '0.8rem', fontWeight: 600, color: '#333' }}
                    >
                      Revir:
                    </label>
                    <select
                      id="customer-workspace-estate-select"
                      value={visibleState.selectedEstateId}
                      onChange={(e) => chooseEstate(e.target.value)}
                      aria-label="Vælg kundens revir"
                      style={{
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        background: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      {visibleState.estates.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <label>
                  <input
                    type="checkbox"
                    checked={advanced}
                    onChange={(event) => setAdvanced(event.target.checked)}
                  />{' '}
                  Flere værktøjer
                </label>
                <button
                  type="button"
                  className="button secondary"
                  onClick={() => navigate('customer-hub')}
                  style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <ArrowLeft size={15} />
                  <span>Kundens revir-oversigt</span>
                </button>
                <button
                  type="button"
                  className="button secondary"
                  onClick={leaveCustomer}
                  style={{ minHeight: 44 }}
                >
                  {isAdmin ? 'Tilbage til administration' : 'Tilbage til mine kunder'}
                </button>
              </section>
            )}
            {syncBanners}
            {page === 'map' ? (
              <section className="map-workspace">
                <div className="workspace-heading">
                  <div>
                    <p className="eyebrow">MARKPLANLÆGNING</p>
                    <h1>
                      Mine marker<span className="title-dot">.</span>
                    </h1>
                    <p className="muted">
                      Tegn dit areal. Vælg din blanding. Se præcis, hvad du skal bruge.
                    </p>
                  </div>
                  <div className="estate-picker">
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                    >
                      <label htmlFor="estate-select">REVIR</label>
                      <select
                        id="estate-select"
                        value={
                          visibleState.selectedEstateId || (allowEstateCreation ? '__new__' : '')
                        }
                        onChange={(e) => {
                          if (e.target.value === '__new__') {
                            setIsCreateEstateModalOpen(true);
                          } else {
                            chooseEstate(e.target.value);
                          }
                        }}
                      >
                        {visibleState.estates.length === 0 && (
                          <option value="" disabled>
                            Intet revir oprettet endnu
                          </option>
                        )}
                        {visibleState.estates.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.name}
                          </option>
                        ))}
                        {allowEstateCreation && <option value="__new__">+ Nyt revir…</option>}
                      </select>
                      {allowEstateCreation && (
                        <button
                          type="button"
                          className="button secondary"
                          onClick={() => setIsCreateEstateModalOpen(true)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            height: 36,
                            padding: '0 12px',
                            fontSize: 13,
                            fontWeight: 500,
                          }}
                          title="Opret nyt revir med adresse og kortplacering"
                        >
                          <Plus size={14} /> Nyt revir
                        </button>
                      )}
                    </div>
                    <span>
                      {estate?.address ||
                        (isFreeAccount
                          ? 'Find dit areal med adressesøgningen på kortet'
                          : 'Opret dit revir for at komme i gang')}
                    </span>
                  </div>
                </div>
                {estate ? (
                  <div
                    className={`planning-layout ${isFieldPanelCollapsed ? 'panel-collapsed' : ''}`}
                  >
                    <aside className="field-panel">
                      <div className="field-panel-heading">
                        <div className="field-panel-title-wrap">
                          <h2>
                            Dine arealer <span>{fields.length}</span>
                          </h2>
                          <p>
                            {totalVildtagerArea > 0 && `${number(totalVildtagerArea)} ha marker`}
                            {totalVildtagerArea > 0 && totalZoneArea > 0 && ' · '}
                            {totalZoneArea > 0 &&
                              `${number(totalZoneArea)} ha ${zoneFields.length === 1 ? 'område' : 'områder'}`}
                            {totalVildtagerArea === 0 && totalZoneArea === 0 && '0 ha marker'}
                          </p>
                        </div>
                        <div className="field-panel-header-actions">
                          <button
                            type="button"
                            className="field-panel-toggle-btn"
                            onClick={() => setIsFieldPanelCollapsed(true)}
                            title="Skjul arealliste (mere plads til kort)"
                            aria-label="Skjul arealliste"
                          >
                            <ChevronLeft size={16} />
                          </button>
                        </div>
                      </div>
                      {!isFreeAccount &&
                        state.plans.filter((p) => p.estateId === estate.id).length > 1 && (
                          <label className="plan-picker">
                            Plejeplan
                            <select
                              value={state.selectedPlanId}
                              onChange={(e) =>
                                setState((s) => ({ ...s, selectedPlanId: e.target.value }))
                              }
                            >
                              {state.plans
                                .filter((p) => p.estateId === estate.id)
                                .map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name} · {p.year}
                                  </option>
                                ))}
                            </select>
                          </label>
                        )}
                      <div className="field-list">
                        {visibleState.estates.length === 0 ? (
                          <div
                            className="empty-field"
                            style={{ padding: '24px 16px', textAlign: 'center' }}
                          >
                            <MapPin size={29} style={{ margin: '0 auto 8px', color: '#86efac' }} />
                            <h3 style={{ fontSize: 15, marginBottom: 4 }}>Begynd med dit revir</h3>
                            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
                              Opret dit revir for at tegne dine marker på kortet.
                            </p>
                            {allowEstateCreation && (
                              <button
                                type="button"
                                className="button primary"
                                onClick={() => setIsCreateEstateModalOpen(true)}
                                style={{ margin: '0 auto' }}
                              >
                                <Plus size={14} /> Opret revir
                              </button>
                            )}
                          </div>
                        ) : fields.length === 0 ? (
                          <div className="empty-field">
                            <Sprout size={29} />
                            <h3>{isFreeAccount ? 'Din første vildtager' : 'Din første mark'}</h3>
                            <p>
                              Tryk på {isFreeAccount ? 'Tegn vildtager' : 'Tegn mark'}, og sæt
                              hjørnerne på kortet.
                            </p>
                          </div>
                        ) : (
                          (() => {
                            const zoneList = fields.filter((f) => isZoneField(f));
                            const vildtagerList = fields.filter((f) => isVildtagerField(f));
                            const otherList = fields.filter((f) => isOtherPolygonField(f));
                            const poiList = fields.filter((f) => isJagtPoiField(f));

                            return (
                              <>
                                {/* 1. Område (Altid øverst) */}
                                <div className="omraade-pinned-card">
                                  {zoneList.length > 0 ? (
                                    zoneList.map((field) => (
                                      <button
                                        key={field.id}
                                        className={`field-row is-zone-row ${selectedFieldId === field.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedFieldId(field.id)}
                                        title="Vælg revirets områdegrænse"
                                      >
                                        <span
                                          className="field-number"
                                          style={{
                                            borderColor: '#ef4444',
                                            background: '#fee2e2',
                                            color: '#b91c1c',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <svg
                                            width={13}
                                            height={13}
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#dc2626"
                                            strokeWidth="2.4"
                                            strokeDasharray="4 2.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                          >
                                            <polygon points="12 3 21 8.5 18 19.5 6 19.5 3 8.5" />
                                          </svg>
                                        </span>
                                        <span>
                                          <strong>{field.name || 'Område'}</strong>
                                          <small style={{ color: '#b91c1c', fontWeight: 600 }}>
                                            Område (revirgrænse)
                                          </small>
                                        </span>
                                        <span className="field-area">
                                          {number(fieldAreaHa(field.geometry))}
                                          <small>ha</small>
                                        </span>
                                      </button>
                                    ))
                                  ) : (
                                    <button
                                      type="button"
                                      className="empty-omraade-btn"
                                      onClick={() => {
                                        const zoneBtn = document.querySelector(
                                          '.map-actionbar-zone-btn',
                                        ) as HTMLButtonElement | null;
                                        if (zoneBtn) {
                                          zoneBtn.click();
                                        } else {
                                          startDrawingFromInspector('zone');
                                        }
                                      }}
                                      title="Tegn revirets område for at afgrænse reviret"
                                    >
                                      <span
                                        className="field-number"
                                        style={{
                                          borderColor: '#fca5a5',
                                          background: '#fff5f5',
                                          color: '#dc2626',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                      >
                                        <svg
                                          width={13}
                                          height={13}
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="#dc2626"
                                          strokeWidth="2.4"
                                          strokeDasharray="4 2.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          aria-hidden="true"
                                        >
                                          <polygon points="12 3 21 8.5 18 19.5 6 19.5 3 8.5" />
                                        </svg>
                                      </span>
                                      <span>
                                        <strong style={{ color: '#991b1b' }}>Område</strong>
                                        <small style={{ color: '#b91c1c' }}>
                                          + Tegn revirets grænse
                                        </small>
                                      </span>
                                      <span className="field-area">
                                        <Plus size={14} style={{ color: '#dc2626' }} />
                                      </span>
                                    </button>
                                  )}
                                </div>

                                {/* 2. Vildtagre (Accordion) */}
                                <div className="field-category-group">
                                  <button
                                    type="button"
                                    className="field-category-header"
                                    onClick={() => toggleSection('vildtagre')}
                                    aria-expanded={expandedSections.vildtagre}
                                  >
                                    <div className="field-category-title">
                                      <Sprout size={14} style={{ color: '#15803d' }} />
                                      <span>Vildtagre</span>
                                      <span className="field-category-count">
                                        {vildtagerList.length}
                                      </span>
                                    </div>
                                    <ChevronDown
                                      size={15}
                                      className={`field-category-chevron ${expandedSections.vildtagre ? 'is-open' : ''}`}
                                    />
                                  </button>
                                  {expandedSections.vildtagre && (
                                    <div className="field-category-content">
                                      {vildtagerList.length === 0 ? (
                                        <div className="field-category-empty">
                                          Ingen vildtagre optegnet endnu
                                        </div>
                                      ) : (
                                        vildtagerList.map((field, index) => {
                                          const product = products.find(
                                            (p) => p.id === plan?.selections[field.id],
                                          );
                                          return (
                                            <button
                                              key={field.id}
                                              className={`field-row ${selectedFieldId === field.id ? 'selected' : ''}`}
                                              onClick={() => setSelectedFieldId(field.id)}
                                            >
                                              <span
                                                className="field-number"
                                                style={{
                                                  borderColor: blendColor(product),
                                                  background: `color-mix(in srgb, ${blendColor(product)} 18%, white)`,
                                                  color: '#22362c',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                }}
                                              >
                                                {String(index + 1).padStart(2, '0')}
                                              </span>
                                              <span>
                                                <strong>{field.name}</strong>
                                                <small>
                                                  {product
                                                    ? product.name
                                                    : isFreeAccount
                                                      ? 'Kladde · vælg frøblanding'
                                                      : 'Vælg frøblanding'}
                                                </small>
                                              </span>
                                              <span className="field-area">
                                                {number(fieldAreaHa(field.geometry))}
                                                <small>ha</small>
                                              </span>
                                            </button>
                                          );
                                        })
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* 3. Arealer (Accordion) */}
                                <div className="field-category-group">
                                  <button
                                    type="button"
                                    className="field-category-header"
                                    onClick={() => toggleSection('oevrige')}
                                    aria-expanded={expandedSections.oevrige}
                                  >
                                    <div className="field-category-title">
                                      <LandPlot size={14} style={{ color: '#166534' }} />
                                      <span>Arealer</span>
                                      <span className="field-category-count">
                                        {otherList.length}
                                      </span>
                                    </div>
                                    <ChevronDown
                                      size={15}
                                      className={`field-category-chevron ${expandedSections.oevrige ? 'is-open' : ''}`}
                                    />
                                  </button>
                                  {expandedSections.oevrige && (
                                    <div className="field-category-content">
                                      {otherList.length === 0 ? (
                                        <div className="field-category-empty">
                                          Ingen arealer endnu
                                        </div>
                                      ) : (
                                        otherList.map((field) => {
                                          const areaDef = getAreaTypeDefinition(
                                            field.areaType,
                                            field.kind,
                                          );
                                          const isLine =
                                            field.areaType === 'laehegn' ||
                                            Boolean(field.lineLengthMeters) ||
                                            field.notes?.includes('[LINJE:');
                                          const lineM =
                                            field.lineLengthMeters ??
                                            (field.notes?.match(/\[LINJE:(\d+)\]/)?.[1]
                                              ? parseInt(
                                                  field.notes.match(/\[LINJE:(\d+)\]/)![1],
                                                  10,
                                                )
                                              : Math.round(
                                                  (fieldAreaHa(field.geometry) * 10000) / 6,
                                                ));
                                          return (
                                            <button
                                              key={field.id}
                                              className={`field-row ${selectedFieldId === field.id ? 'selected' : ''}`}
                                              onClick={() => setSelectedFieldId(field.id)}
                                            >
                                              <span
                                                className="field-number"
                                                style={{
                                                  borderColor: areaDef.strokeColor,
                                                  background: `color-mix(in srgb, ${areaDef.color} 22%, white)`,
                                                  color: areaDef.strokeColor,
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    display: 'inline-flex',
                                                    width: 12,
                                                    height: 12,
                                                  }}
                                                  dangerouslySetInnerHTML={{
                                                    __html: areaDef.symbolSvg,
                                                  }}
                                                />
                                              </span>
                                              <span>
                                                <strong>{field.name}</strong>
                                                <small
                                                  style={{
                                                    color: areaDef.strokeColor,
                                                    fontWeight: 500,
                                                  }}
                                                >
                                                  {areaDef.label}
                                                </small>
                                              </span>
                                              <span className="field-area">
                                                {isLine ? (
                                                  <>
                                                    {lineM} <small>m</small>
                                                  </>
                                                ) : (
                                                  <>
                                                    {number(fieldAreaHa(field.geometry))}{' '}
                                                    <small>ha</small>
                                                  </>
                                                )}
                                              </span>
                                            </button>
                                          );
                                        })
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* 4. Punkter (Accordion) */}
                                <div className="field-category-group">
                                  <button
                                    type="button"
                                    className="field-category-header"
                                    onClick={() => toggleSection('kortpunkter')}
                                    aria-expanded={expandedSections.kortpunkter}
                                  >
                                    <div className="field-category-title">
                                      <MapPin size={14} style={{ color: '#d97706' }} />
                                      <span>Punkter</span>
                                      <span className="field-category-count">{poiList.length}</span>
                                    </div>
                                    <ChevronDown
                                      size={15}
                                      className={`field-category-chevron ${expandedSections.kortpunkter ? 'is-open' : ''}`}
                                    />
                                  </button>
                                  {expandedSections.kortpunkter && (
                                    <div className="field-category-content">
                                      {poiList.length === 0 ? (
                                        <div className="field-category-empty">
                                          Ingen punkter endnu
                                        </div>
                                      ) : (
                                        poiList.map((field) => {
                                          const poi = parseJagtPoi(field.notes);
                                          const poiConfig = poi ? JAGT_POI_CONFIGS[poi.type] : null;
                                          return (
                                            <button
                                              key={field.id}
                                              className={`field-row ${selectedFieldId === field.id ? 'selected' : ''}`}
                                              onClick={() => setSelectedFieldId(field.id)}
                                            >
                                              <span
                                                className="field-number"
                                                style={{
                                                  borderColor: poiConfig
                                                    ? poiConfig.color
                                                    : '#d97706',
                                                  background: poiConfig
                                                    ? `color-mix(in srgb, ${poiConfig.color} 20%, white)`
                                                    : '#fffbeb',
                                                  color: poiConfig ? poiConfig.color : '#d97706',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                }}
                                              >
                                                {poiConfig ? (
                                                  <span
                                                    style={{
                                                      display: 'inline-flex',
                                                      width: 14,
                                                      height: 14,
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                      __html: poiConfig.svgIcon,
                                                    }}
                                                  />
                                                ) : (
                                                  <Crosshair size={13} />
                                                )}
                                              </span>
                                              <span>
                                                <strong>{stripJagtPoiPrefix(field.name)}</strong>
                                                <small
                                                  style={{
                                                    color: poiConfig?.color || '#d97706',
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {poiConfig ? poiConfig.label : 'Kortpunkt'}
                                                  {poi?.direction ? ` (${poi.direction})` : ''}
                                                </small>
                                              </span>
                                              <span className="field-area">
                                                <small>PUNKT</small>
                                              </span>
                                            </button>
                                          );
                                        })
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            );
                          })()
                        )}
                      </div>
                      {selected &&
                        (isEditingShape ? (
                          <div className="field-inspector is-editing-shape">
                            <div className="editing-shape-banner">
                              <div className="editing-shape-text">
                                <span
                                  className="editing-shape-badge"
                                  style={
                                    isZoneField(selected)
                                      ? { background: '#fee2e2', color: '#b91c1c' }
                                      : isNatureOrZoneField(selected)
                                        ? {
                                            background: `color-mix(in srgb, ${getAreaTypeDefinition(selected.areaType, selected.kind).color} 22%, white)`,
                                            color: getAreaTypeDefinition(
                                              selected.areaType,
                                              selected.kind,
                                            ).strokeColor,
                                          }
                                        : undefined
                                  }
                                >
                                  {isZoneField(selected)
                                    ? 'Tilpas jagtområde'
                                    : isNatureOrZoneField(selected)
                                      ? `Tilpas ${getAreaTypeDefinition(selected.areaType, selected.kind).label.toLowerCase()}`
                                      : 'Tilpas facon'}
                                </span>
                                <strong>{selected.name}</strong>
                                <small>Træk i de hvide hjørnepunkter for at ændre formen</small>
                              </div>
                              <button
                                type="button"
                                className="button primary editing-shape-done-btn"
                                onClick={() => setIsEditingShape(false)}
                              >
                                <Check size={16} /> Færdig
                              </button>
                            </div>
                          </div>
                        ) : parseJagtPoi(selected.notes) ? (
                          (() => {
                            const poi = parseJagtPoi(selected.notes)!;
                            const poiConfig = JAGT_POI_CONFIGS[poi.type];

                            if (poi.type === 'jordmaaler') {
                              return (
                                <PoiSoilSensorInspector
                                  field={selected}
                                  poi={poi}
                                  poiConfig={poiConfig}
                                  planReadOnly={planReadOnly}
                                  updateField={updateField}
                                  deleteField={deleteField}
                                  onClose={() => setSelectedFieldId('')}
                                  onNavigateToSeeding={() => navigate('seeding')}
                                  notify={notify}
                                />
                              );
                            }

                            if (poi.type === 'vildtkamera' || poi.type === 'faeldesensor') {
                              return (
                                <PoiIntegrationInspector
                                  field={selected}
                                  poi={poi}
                                  poiConfig={poiConfig}
                                  estate={estate || state.estates[0]}
                                  planReadOnly={planReadOnly}
                                  updateField={updateField}
                                  deleteField={deleteField}
                                  onClose={() => setSelectedFieldId('')}
                                  onNavigateToIntegrations={(tab) => {
                                    try {
                                      sessionStorage.setItem('plotwise.activeIntegrationTab', tab);
                                    } catch {}
                                    navigate('integrations');
                                  }}
                                  notify={notify}
                                />
                              );
                            }

                            return (
                              <div className="field-inspector is-poi-inspector">
                                <div className="sheet-handle" aria-hidden="true" />
                                <div className="section-heading">
                                  <h3>
                                    <span
                                      style={{
                                        display: 'inline-flex',
                                        verticalAlign: -3,
                                        marginRight: 6,
                                        color: poiConfig.color,
                                      }}
                                      dangerouslySetInnerHTML={{ __html: poiConfig.svgIcon }}
                                    />
                                    {poiConfig.label}
                                  </h3>
                                  <button
                                    className="icon-button"
                                    aria-label="Luk oplysninger"
                                    onClick={() => setSelectedFieldId('')}
                                  >
                                    <X size={17} />
                                  </button>
                                </div>
                                <div
                                  style={{
                                    background: `color-mix(in srgb, ${poiConfig.color} 10%, white)`,
                                    border: `1px solid color-mix(in srgb, ${poiConfig.color} 30%, white)`,
                                    borderRadius: 8,
                                    padding: '10px 12px',
                                    marginBottom: 12,
                                    fontSize: 12,
                                    color: poiConfig.color,
                                    lineHeight: 1.4,
                                  }}
                                >
                                  <strong>{poiConfig.label}</strong>
                                  <div style={{ marginTop: 2, opacity: 0.9 }}>
                                    Kortpunkt på reviret. Placeringen er markeret på kortet.
                                  </div>
                                  <div style={{ marginTop: 6, fontWeight: 600 }}>
                                    💡 Træk i punktet på kortet for at flytte placering
                                  </div>
                                </div>
                                <label className="field-label">
                                  Navn på {poiConfig.label.toLowerCase()}
                                  <input
                                    className="form-input"
                                    disabled={planReadOnly}
                                    value={selected.name}
                                    onChange={(e) =>
                                      updateField(selected.id, { name: e.target.value })
                                    }
                                    onBlur={() => {
                                      if (!selected.name.trim())
                                        updateField(selected.id, { name: poiConfig.defaultName });
                                    }}
                                  />
                                </label>
                                {poi.type === 'skydetaarn' && (
                                  <div style={{ marginTop: 12 }}>
                                    <label className="field-label" style={{ marginBottom: 6 }}>
                                      Skydningsretning / Udsyn
                                    </label>
                                    <div
                                      className="poi-compass-grid"
                                      style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                        gap: 6,
                                        maxWidth: 260,
                                        margin: '0 auto',
                                      }}
                                    >
                                      {(
                                        [
                                          'NV',
                                          'N',
                                          'NØ',
                                          'V',
                                          'Alle',
                                          'Ø',
                                          'SV',
                                          'S',
                                          'SØ',
                                        ] as ShootingDirection[]
                                      ).map((dir) => {
                                        const isAlle = dir === 'Alle';
                                        const currentDirs =
                                          poi.directions || (poi.direction ? [poi.direction] : []);
                                        const isActive = isAlle
                                          ? currentDirs.length === 0 || currentDirs.includes('Alle')
                                          : currentDirs.includes(dir);

                                        return (
                                          <button
                                            key={dir}
                                            type="button"
                                            disabled={planReadOnly}
                                            className={`button secondary poi-compass-btn ${isActive ? 'primary' : ''}`}
                                            style={{
                                              minHeight: 38,
                                              padding: '6px 4px',
                                              fontSize: 12,
                                              fontWeight: 700,
                                              textAlign: 'center',
                                              justifyContent: 'center',
                                              background: isActive ? '#d97706' : undefined,
                                              borderColor: isActive ? '#b45309' : undefined,
                                              color: isActive ? '#fff' : undefined,
                                            }}
                                            onClick={() => {
                                              let nextDirs: ShootingDirection[];
                                              if (dir === 'Alle') {
                                                nextDirs = ['Alle'];
                                              } else {
                                                const withoutAlle = currentDirs.filter(
                                                  (d) => d !== 'Alle',
                                                );
                                                if (withoutAlle.includes(dir)) {
                                                  nextDirs = withoutAlle.filter((d) => d !== dir);
                                                  if (nextDirs.length === 0) nextDirs = ['Alle'];
                                                } else {
                                                  nextDirs = [...withoutAlle, dir];
                                                }
                                              }
                                              const newNotes = formatJagtPoiNotes(
                                                poi.type,
                                                nextDirs,
                                                poi.userNotes,
                                              );
                                              updateField(selected.id, { notes: newNotes });
                                            }}
                                          >
                                            {dir}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                <label className="field-label" style={{ marginTop: 12 }}>
                                  Noter (placering, stand, udsyn)
                                  <textarea
                                    className="form-input"
                                    disabled={planReadOnly}
                                    rows={2}
                                    value={poi.userNotes}
                                    onChange={(e) => {
                                      const currentDirs =
                                        poi.directions || (poi.direction ? [poi.direction] : []);
                                      const newNotes = formatJagtPoiNotes(
                                        poi.type,
                                        currentDirs,
                                        e.target.value,
                                      );
                                      updateField(selected.id, { notes: newNotes });
                                    }}
                                    placeholder="Terræn, stand, højde, udsyn…"
                                  />
                                </label>
                                <div className="inspector-foot" style={{ marginTop: 16 }}>
                                  <button
                                    className="text-button danger"
                                    disabled={planReadOnly}
                                    onClick={() => deleteField(selected)}
                                  >
                                    <Trash2 size={14} />
                                    Slet {poiConfig.label.toLowerCase()}
                                  </button>
                                </div>
                                <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                                  <button
                                    type="button"
                                    className="button primary"
                                    style={{ width: '100%', padding: '8px 12px', fontSize: 13 }}
                                    onClick={() => setSelectedFieldId('')}
                                  >
                                    Luk
                                  </button>
                                </div>
                              </div>
                            );
                          })()
                        ) : isZoneField(selected) ? (
                          <div className="field-inspector is-zone-inspector">
                            <div className="sheet-handle" aria-hidden="true" />
                            <div className="section-heading">
                              <h3>
                                <svg
                                  width={16}
                                  height={16}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#dc2626"
                                  strokeWidth="2.2"
                                  strokeDasharray="4 2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  style={{ verticalAlign: -2, marginRight: 6 }}
                                  aria-hidden="true"
                                >
                                  <polygon points="12 3 21 8.5 18 19.5 6 19.5 3 8.5" />
                                </svg>
                                Område
                              </h3>
                              <button
                                className="icon-button"
                                aria-label="Luk områdeoplysninger"
                                onClick={() => setSelectedFieldId('')}
                              >
                                <X size={17} />
                              </button>
                            </div>
                            <div
                              className="zone-info-banner"
                              style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: 8,
                                padding: '10px 12px',
                                marginBottom: 12,
                                fontSize: 12,
                                color: '#991b1b',
                                lineHeight: 1.4,
                              }}
                            >
                              <strong>Revirgrænse</strong>
                              <div style={{ marginTop: 2, opacity: 0.9 }}>
                                Definerer revirets ydre jagtområde. Vildtagre tegnes med &quot;+
                                Vildtager&quot; i bunden.
                              </div>
                            </div>
                            <label className="field-label">
                              Navn på område
                              <input
                                className="form-input"
                                disabled={planReadOnly}
                                value={selected.name}
                                onChange={(e) => updateField(selected.id, { name: e.target.value })}
                                onBlur={() => {
                                  if (!selected.name.trim())
                                    updateField(selected.id, { name: 'Område' });
                                }}
                              />
                            </label>

                            <div className="field-shape-actions">
                              <button
                                type="button"
                                className="button secondary shape-edit-trigger"
                                disabled={planReadOnly}
                                onClick={() => setIsEditingShape(true)}
                              >
                                <Pencil size={15} />
                                Tilpas facon / flyt hjørner
                              </button>
                            </div>

                            <div className="inspector-foot">
                              <span>
                                {number(fieldAreaHa(selected.geometry))} ha (
                                {number(fieldAreaHa(selected.geometry) * 10000, 0)} m²)
                              </span>
                              <button
                                className="text-button danger"
                                disabled={planReadOnly}
                                onClick={() => deleteField(selected)}
                              >
                                <Trash2 size={14} />
                                Slet område
                              </button>
                            </div>
                            <div style={{ marginTop: 12 }}>
                              <button
                                type="button"
                                className="button primary"
                                style={{ width: '100%', padding: '8px 12px', fontSize: 13 }}
                                onClick={() => setSelectedFieldId('')}
                              >
                                Luk område
                              </button>
                            </div>
                          </div>
                        ) : isNatureOrZoneField(selected) ? (
                          (() => {
                            const isLine =
                              selected.notes?.includes('[LINJE') ||
                              selected.areaType === 'laehegn' ||
                              Boolean(selected.lineLengthMeters) ||
                              Boolean(selected.lineWidthMeters);
                            const currentColor =
                              selected.customColor ||
                              (isLine
                                ? '#2e7d32'
                                : getAreaTypeDefinition(selected.areaType).color || '#397A46');
                            const currentOpacity = selected.customOpacity ?? 0.4;
                            const currentStrokeWidth = selected.strokeWidth ?? 2;
                            const lineLength =
                              selected.lineLengthMeters ??
                              (isLine
                                ? calculateLineLengthMeters(
                                    selected.geometry?.coordinates?.[0] || [],
                                  )
                                : 0);

                            return (
                              <div className="field-inspector is-zone-inspector">
                                <div className="sheet-handle" aria-hidden="true" />
                                <div className="section-heading">
                                  <h3>
                                    <LandPlot
                                      size={16}
                                      color={currentColor}
                                      style={{ verticalAlign: -2, marginRight: 6 }}
                                    />
                                    {isLine ? 'Linje' : 'Areal'}
                                  </h3>
                                  <button
                                    className="icon-button"
                                    aria-label={
                                      isLine ? 'Luk linjeoplysninger' : 'Luk arealoplysninger'
                                    }
                                    onClick={() => setSelectedFieldId('')}
                                  >
                                    <X size={17} />
                                  </button>
                                </div>

                                <label className="field-label" style={{ marginTop: 8 }}>
                                  Navn på {isLine ? 'linje' : 'areal'}
                                  <input
                                    className="form-input"
                                    disabled={planReadOnly}
                                    placeholder={
                                      isLine
                                        ? 'f.eks. Læhegn vest, Markskel nord...'
                                        : 'f.eks. Remisse, Tætslået græs, Skov...'
                                    }
                                    value={selected.name}
                                    onChange={(e) =>
                                      updateField(selected.id, { name: e.target.value })
                                    }
                                    onBlur={() => {
                                      if (!selected.name.trim())
                                        updateField(selected.id, {
                                          name: isLine ? 'Linje' : 'Areal',
                                        });
                                    }}
                                  />
                                </label>

                                <div className="field-shape-actions">
                                  <button
                                    type="button"
                                    className="button secondary shape-edit-trigger"
                                    disabled={planReadOnly}
                                    onClick={() => setIsEditingShape(true)}
                                  >
                                    <Pencil size={15} />
                                    Tilpas facon / flyt hjørner
                                  </button>
                                </div>

                                {/* Farve & Visning under Rediger */}
                                <div
                                  className="field-style-section"
                                  style={{
                                    marginTop: 12,
                                    padding: '10px 12px',
                                    background: '#f8fafc',
                                    borderRadius: 8,
                                    border: '1px solid #e2e8f0',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      marginBottom: 8,
                                    }}
                                  >
                                    <span
                                      style={{ fontSize: 12, fontWeight: 650, color: '#334155' }}
                                    >
                                      Farve
                                    </span>
                                    <label
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        fontSize: 11,
                                        color: '#64748b',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <span>Vælg fri farve</span>
                                      <input
                                        type="color"
                                        disabled={planReadOnly}
                                        value={currentColor}
                                        onChange={(e) => {
                                          const hex = e.target.value;
                                          updateField(selected.id, {
                                            customColor: hex,
                                            customStrokeColor: getDarkerShade(hex),
                                          });
                                        }}
                                        style={{
                                          width: 24,
                                          height: 24,
                                          padding: 0,
                                          border: 'none',
                                          borderRadius: 4,
                                          cursor: 'pointer',
                                        }}
                                      />
                                    </label>
                                  </div>
                                  <div
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(7, 1fr)',
                                      gap: 6,
                                      marginBottom: 12,
                                    }}
                                  >
                                    {[
                                      { name: 'Tætslået græs', hex: '#9ACD32' },
                                      { name: 'Brak', hex: '#E6C66B' },
                                      { name: 'Remisse', hex: '#397A46' },
                                      { name: 'Skov', hex: '#20523A' },
                                      { name: 'Barjordsstribe', hex: '#B68A5A' },
                                      { name: 'Vådområde', hex: '#5CA8C8' },
                                      { name: 'Andet areal', hex: '#9675A9' },
                                    ].map((preset) => (
                                      <button
                                        key={preset.hex}
                                        type="button"
                                        title={`${preset.name} (${preset.hex})`}
                                        disabled={planReadOnly}
                                        onClick={() =>
                                          updateField(selected.id, {
                                            customColor: preset.hex,
                                            customStrokeColor: getDarkerShade(preset.hex),
                                          })
                                        }
                                        style={{
                                          height: 24,
                                          borderRadius: 4,
                                          backgroundColor: preset.hex,
                                          border:
                                            currentColor.toLowerCase() === preset.hex.toLowerCase()
                                              ? '2px solid #0f172a'
                                              : '1px solid rgba(0,0,0,0.15)',
                                          cursor: 'pointer',
                                          outline: 'none',
                                          padding: 0,
                                        }}
                                      />
                                    ))}
                                  </div>

                                  {!isLine ? (
                                    <div style={{ marginTop: 6 }}>
                                      <div
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          fontSize: 11,
                                          color: '#64748b',
                                          marginBottom: 4,
                                        }}
                                      >
                                        <span>Gennemsigtighed</span>
                                        <span style={{ fontWeight: 600, color: '#334155' }}>
                                          {Math.round(currentOpacity * 100)}%
                                        </span>
                                      </div>
                                      <input
                                        type="range"
                                        min="10"
                                        max="100"
                                        step="5"
                                        disabled={planReadOnly}
                                        value={Math.round(currentOpacity * 100)}
                                        onChange={(e) =>
                                          updateField(selected.id, {
                                            customOpacity: Number(e.target.value) / 100,
                                          })
                                        }
                                        style={{
                                          width: '100%',
                                          accentColor: '#166534',
                                          cursor: 'pointer',
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <div style={{ marginTop: 6 }}>
                                      <div
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          fontSize: 11,
                                          color: '#64748b',
                                          marginBottom: 4,
                                        }}
                                      >
                                        <span>Linjetykkelse</span>
                                        <span style={{ fontWeight: 600, color: '#334155' }}>
                                          {currentStrokeWidth} px
                                        </span>
                                      </div>
                                      <input
                                        type="range"
                                        min="1"
                                        max="6"
                                        step="1"
                                        disabled={planReadOnly}
                                        value={currentStrokeWidth}
                                        onChange={(e) =>
                                          updateField(selected.id, {
                                            strokeWidth: Number(e.target.value),
                                          })
                                        }
                                        style={{
                                          width: '100%',
                                          accentColor: '#166534',
                                          cursor: 'pointer',
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="inspector-foot">
                                  <span>
                                    {isLine
                                      ? `${lineLength} meter (${number(fieldAreaHa(selected.geometry))} ha / ${number(fieldAreaHa(selected.geometry) * 10000, 0)} m² · ${selected.lineWidthMeters || (selected.notes?.match(/BREDDE:(\d+(\.\d+)?)/)?.[1] ? parseFloat(selected.notes.match(/BREDDE:(\d+(\.\d+)?)/)![1]) : 2)} m bred)`
                                      : `${number(fieldAreaHa(selected.geometry))} ha (${number(fieldAreaHa(selected.geometry) * 10000, 0)} m²)`}
                                  </span>
                                  <button
                                    className="text-button danger"
                                    disabled={planReadOnly}
                                    onClick={() => deleteField(selected)}
                                  >
                                    <Trash2 size={14} />
                                    {isLine ? 'Slet linje' : 'Slet areal'}
                                  </button>
                                </div>
                                <div style={{ marginTop: 12 }}>
                                  <button
                                    type="button"
                                    className="button primary"
                                    style={{ width: '100%', padding: '8px 12px', fontSize: 13 }}
                                    onClick={() => setSelectedFieldId('')}
                                  >
                                    Luk
                                  </button>
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <div className="field-inspector">
                            <div className="sheet-handle" aria-hidden="true" />
                            <div className="section-heading">
                              <h3>Rediger mark</h3>
                              <button
                                className="icon-button"
                                aria-label="Luk markoplysninger"
                                onClick={() => setSelectedFieldId('')}
                              >
                                <X size={17} />
                              </button>
                            </div>
                            {/* Mobil: handlingslinjen ligger bag dette skema, saa tegne-
                              handlingerne skal vaere et tryk vaek her i stedet. */}
                            <div className="inspector-draw-actions">
                              <button
                                type="button"
                                className="button primary"
                                disabled={planReadOnly}
                                onClick={() => startDrawingFromInspector('field')}
                              >
                                <Plus size={16} />
                                Tegn revir
                              </button>
                              <button
                                type="button"
                                className="button secondary"
                                disabled={planReadOnly}
                                onClick={() => startDrawingFromInspector('zone')}
                              >
                                <svg
                                  width={16}
                                  height={16}
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#dc2626"
                                  strokeWidth="2.2"
                                  strokeDasharray="4 2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <polygon points="12 3 21 8.5 18 19.5 6 19.5 3 8.5" />
                                </svg>
                                Tegn område
                              </button>
                            </div>
                            <label className="field-label">
                              Navn
                              <input
                                className="form-input"
                                disabled={planReadOnly}
                                value={selected.name}
                                onChange={(e) => updateField(selected.id, { name: e.target.value })}
                                onBlur={() => {
                                  if (!selected.name.trim())
                                    updateField(selected.id, { name: 'Unavngivet mark' });
                                }}
                              />
                            </label>

                            <details
                              className="field-convert-disclosure"
                              style={{ marginBottom: 12 }}
                            >
                              <summary className="field-notes-summary">
                                <Shield size={13} />
                                <span>Skift til område (valgfrit)</span>
                              </summary>
                              <label className="field-label" style={{ marginTop: 6 }}>
                                Vælg områdetype
                                <select
                                  className="form-input"
                                  disabled={planReadOnly}
                                  value="mark"
                                  onChange={(e) => {
                                    const newType = e.target.value as AreaType;
                                    if (newType !== 'mark') {
                                      updateField(selected.id, {
                                        areaType: newType,
                                        kind: newType === 'jagtomraade' ? 'zone' : 'field',
                                      });
                                    }
                                  }}
                                >
                                  <option value="mark">Mark (standard)</option>
                                  {AREA_TYPES.map((t) => (
                                    <option key={t.id} value={t.id}>
                                      {t.label}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </details>

                            <div className="purpose-chips-section">
                              <span className="field-sublabel">Hvad vil du tilgodese?</span>
                              <div className="purpose-chips">
                                {[
                                  { id: 'deer', label: 'Hjortevildt' },
                                  { id: 'pheasant', label: 'Fasan & fugle' },
                                  { id: 'winter', label: 'Dækning & læ' },
                                  { id: 'grass', label: 'Hunters Corn' },
                                  { id: 'brassica', label: 'Bestøverbrak' },
                                ].map((target) => {
                                  const currentSel = plan?.selections[selected.id];
                                  const isSelected = currentSel === target.id;
                                  return (
                                    <button
                                      key={target.id}
                                      type="button"
                                      disabled={planReadOnly}
                                      className={`purpose-chip ${isSelected ? 'active' : ''}`}
                                      onClick={() => {
                                        const nextBlend = isSelected ? '' : target.id;
                                        setState((s) => ({
                                          ...s,
                                          plans: s.plans.map((p) =>
                                            p.id === plan?.id
                                              ? {
                                                  ...p,
                                                  selections: nextBlend
                                                    ? {
                                                        ...p.selections,
                                                        [selected.id]: nextBlend,
                                                      }
                                                    : Object.fromEntries(
                                                        Object.entries(p.selections).filter(
                                                          ([key]) => key !== selected.id,
                                                        ),
                                                      ),
                                                  updatedAt: new Date().toISOString(),
                                                }
                                              : p,
                                          ),
                                        }));
                                      }}
                                    >
                                      {target.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <label className="field-label">
                              Frøblanding
                              <select
                                className="form-input"
                                disabled={planReadOnly}
                                value={plan?.selections[selected.id] ?? ''}
                                onChange={(e) =>
                                  setState((s) => ({
                                    ...s,
                                    plans: s.plans.map((p) =>
                                      p.id === plan?.id
                                        ? {
                                            ...p,
                                            selections: e.target.value
                                              ? { ...p.selections, [selected.id]: e.target.value }
                                              : Object.fromEntries(
                                                  Object.entries(p.selections).filter(
                                                    ([key]) => key !== selected.id,
                                                  ),
                                                ),
                                            updatedAt: new Date().toISOString(),
                                          }
                                        : p,
                                    ),
                                  }))
                                }
                              >
                                <option value="">Vælg blanding manuelt</option>
                                {state.products
                                  .filter((p) => p.active)
                                  .map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.name}
                                    </option>
                                  ))}
                              </select>
                            </label>

                            <div style={{ marginTop: 2, marginBottom: 8 }}>
                              <button
                                type="button"
                                className="link-button"
                                disabled={planReadOnly}
                                onClick={() => {
                                  setAdvisorTargetFieldId(selected.id);
                                  setSelectedFieldId('');
                                  setShowAdvisorModal(true);
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 5,
                                  fontSize: 12,
                                  color: '#2d6a4f',
                                  fontWeight: 600,
                                }}
                              >
                                <Sparkles size={13} />
                                Er du i tvivl? Prøv frøberegneren
                              </button>
                            </div>

                            {(() => {
                              const selectedProd = products.find(
                                (p) => p.id === plan?.selections[selected.id],
                              );
                              if (!selectedProd) return null;
                              const isCorn = isKernelUnit(selectedProd.unit);
                              const photoUrl = selectedProd.imageUrl || selectedProd.fieldPhotoUrl;
                              return (
                                <div className="blend-compact-info">
                                  <div
                                    style={{
                                      display: 'flex',
                                      gap: 10,
                                      alignItems: 'center',
                                      marginBottom: 6,
                                    }}
                                  >
                                    {photoUrl && (
                                      <img
                                        src={photoUrl}
                                        alt={selectedProd.name}
                                        style={{
                                          width: 44,
                                          height: 44,
                                          objectFit: 'contain',
                                          borderRadius: 6,
                                          background: '#f8faf7',
                                          border: '1px solid #dce8d8',
                                          padding: 2,
                                          flexShrink: 0,
                                        }}
                                      />
                                    )}
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <strong
                                        style={{ fontSize: 13, color: '#1b3b22', display: 'block' }}
                                      >
                                        {selectedProd.name}
                                      </strong>
                                      <span className="blend-rate-pill">
                                        {isCorn
                                          ? `${selectedProd.rateKgHa.toLocaleString('da-DK')} korn/ha · ${selectedProd.packKg.toLocaleString('da-DK')} korn/pose`
                                          : `${selectedProd.rateKgHa} kg/ha · ${selectedProd.packKg} kg/pakke`}
                                      </span>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="link-button"
                                    onClick={() => navigate('catalog')}
                                  >
                                    Se katalog &amp; detaljer →
                                  </button>
                                </div>
                              );
                            })()}

                            <FieldSoilWeatherCard
                              centroid={getFieldCentroid(selected.geometry)}
                              selectedBlendId={plan?.selections[selected.id]}
                              products={state.products}
                              onNavigateToSeeding={() => navigate('seeding')}
                            />

                            <div className="field-shape-actions">
                              <button
                                type="button"
                                className="button secondary shape-edit-trigger"
                                disabled={planReadOnly}
                                onClick={() => setIsEditingShape(true)}
                              >
                                <Pencil size={15} />
                                Tilpas facon / flyt hjørner
                              </button>
                            </div>

                            {/* Field notes: shown directly if pro/admin/advanced OR if field already has notes; otherwise behind disclosure */}
                            {effectiveIsProView || advanced || Boolean(selected.notes?.trim()) ? (
                              <label className="field-label">
                                Marknoter
                                <textarea
                                  className="form-input"
                                  disabled={planReadOnly}
                                  rows={2}
                                  value={stripJagtPoiPrefix(selected.notes)}
                                  onChange={(e) =>
                                    updateField(selected.id, { notes: e.target.value })
                                  }
                                  placeholder="Jordbund, adgang eller pleje…"
                                />
                              </label>
                            ) : (
                              <details className="field-notes-disclosure">
                                <summary className="field-notes-summary">
                                  <FileText size={13} />
                                  <span>Tilføj marknote (valgfrit)</span>
                                </summary>
                                <label className="field-label" style={{ marginTop: 6 }}>
                                  <textarea
                                    className="form-input"
                                    disabled={planReadOnly}
                                    rows={2}
                                    value={stripJagtPoiPrefix(selected.notes)}
                                    onChange={(e) =>
                                      updateField(selected.id, { notes: e.target.value })
                                    }
                                    placeholder="Jordbund, adgang eller pleje…"
                                  />
                                </label>
                              </details>
                            )}
                            {isProOrAdmin && (
                              <div
                                style={{
                                  marginTop: 10,
                                  marginBottom: 10,
                                  padding: '8px 10px',
                                  background: selected.advisorOnly ? '#fdf8ea' : '#f8faf8',
                                  borderRadius: 6,
                                  border: `1px solid ${selected.advisorOnly ? '#f3d38c' : '#e2ece3'}`,
                                }}
                              >
                                <label
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    cursor: 'pointer',
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: '#2d4a32',
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={!selected.advisorOnly}
                                    disabled={planReadOnly}
                                    onChange={(e) =>
                                      updateField(selected.id, { advisorOnly: !e.target.checked })
                                    }
                                    style={{ accentColor: '#1b3b22', width: 16, height: 16 }}
                                  />
                                  <span>Synlig for kunden på mobilen</span>
                                </label>
                                {selected.advisorOnly && (
                                  <span
                                    style={{
                                      display: 'block',
                                      fontSize: 11,
                                      color: '#8c5e1e',
                                      marginTop: 4,
                                      lineHeight: 1.3,
                                    }}
                                  >
                                    🔒 Rådgiver-kladde: Skjult for kunden, kun synlig i dit panel.
                                  </span>
                                )}
                              </div>
                            )}
                            <div className="inspector-foot">
                              <span>
                                {selected.lineLengthMeters || selected.notes?.includes('[LINJE:')
                                  ? `${selected.lineLengthMeters ?? calculateLineLengthMeters(selected.geometry?.coordinates?.[0] || [])} meter (${number(fieldAreaHa(selected.geometry))} ha / ${number(fieldAreaHa(selected.geometry) * 10000, 0)} m² · ${selected.lineWidthMeters || (selected.notes?.match(/BREDDE:(\d+(\.\d+)?)/)?.[1] ? parseFloat(selected.notes.match(/BREDDE:(\d+(\.\d+)?)/)![1]) : 2)} m bred)`
                                  : `${number(fieldAreaHa(selected.geometry) * 10000, 0)} m² (${number(fieldAreaHa(selected.geometry))} ha)`}
                              </span>
                              <button
                                className="text-button danger"
                                disabled={planReadOnly}
                                onClick={() => deleteField(selected)}
                              >
                                <Trash2 size={14} />
                                Slet mark
                              </button>
                            </div>
                            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                              <button
                                type="button"
                                className="button secondary"
                                style={{ flex: 1, padding: '8px 12px', fontSize: 13 }}
                                onClick={() => setSelectedFieldId('')}
                              >
                                Luk mark
                              </button>
                              <button
                                type="button"
                                className="button primary"
                                style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  fontSize: 13,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 6,
                                }}
                                onClick={() => {
                                  setSelectedFieldId('');
                                  notify(
                                    plan?.selections[selected.id]
                                      ? `${selected.name} er tilføjet til indkøbslisten. Tegn eller vælg næste mark.`
                                      : `${selected.name} er gemt. Vælg eventuelt en frøblanding, eller tegn næste mark.`,
                                  );
                                }}
                              >
                                {plan?.selections[selected.id] ? (
                                  <>
                                    <ShoppingBag size={14} />
                                    Tilføj til kurv
                                  </>
                                ) : (
                                  <>
                                    <Check size={14} />
                                    Gem uden blanding
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                      {deleted && !planReadOnly && (
                        <button className="button secondary restore-field" onClick={restoreField}>
                          <Undo2 size={16} />
                          Gendan {deleted.field.name}
                        </button>
                      )}
                      {/* GPS-rover export: highlighted for Pro/admin/advanced; behind disclosure for standard */}
                      {effectiveIsProView || advanced ? (
                        <div className="field-export-panel">
                          <div className="field-export-info">
                            <span className="field-export-title">Eksport til GPS-rover</span>
                            <p className="field-export-help">
                              Filen er i EPSG:25832 og kan importeres direkte i en GPS-rover.
                            </p>
                          </div>
                          <div className="field-export-actions">
                            <button
                              type="button"
                              className="button secondary field-export-btn"
                              disabled={fields.length === 0}
                              onClick={() => {
                                downloadDxf(fields, estate.name);
                                notify('DXF-fil (EPSG:25832) til GPS-rover er downloadet.');
                              }}
                            >
                              <Download size={14} /> DXF (til GPS-rover)
                            </button>
                            <button
                              type="button"
                              className="button secondary field-export-btn"
                              disabled={fields.length === 0}
                              onClick={() => {
                                downloadSurveyorCsv(fields, estate.name);
                                notify('Punktliste (CSV) er downloadet.');
                              }}
                            >
                              <Download size={14} /> Punktliste (CSV)
                            </button>
                          </div>
                        </div>
                      ) : (
                        <details className="field-export-disclosure">
                          <summary className="field-export-summary">
                            <Download size={13} />
                            <span>Eksport til GPS-rover (DXF / CSV)</span>
                          </summary>
                          <div className="field-export-panel" style={{ marginTop: 8 }}>
                            <div className="field-export-info">
                              <span className="field-export-title">Eksport til GPS-rover</span>
                              <p className="field-export-help">
                                Filen er i EPSG:25832 og kan importeres direkte i en GPS-rover.
                              </p>
                            </div>
                            <div className="field-export-actions">
                              <button
                                type="button"
                                className="button secondary field-export-btn"
                                disabled={fields.length === 0}
                                onClick={() => {
                                  downloadDxf(fields, estate.name);
                                  notify('DXF-fil (EPSG:25832) til GPS-rover er downloadet.');
                                }}
                              >
                                <Download size={14} /> DXF (til GPS-rover)
                              </button>
                              <button
                                type="button"
                                className="button secondary field-export-btn"
                                disabled={fields.length === 0}
                                onClick={() => {
                                  downloadSurveyorCsv(fields, estate.name);
                                  notify('Punktliste (CSV) er downloadet.');
                                }}
                              >
                                <Download size={14} /> Punktliste (CSV)
                              </button>
                            </div>
                          </div>
                        </details>
                      )}
                      <div className="field-panel-footer">
                        <div>
                          <span>
                            {totalVildtagerArea > 0 && zoneFields.length > 0
                              ? 'Vildtagerareal'
                              : zoneFields.length > 0 && totalVildtagerArea === 0
                                ? 'Områdeareal'
                                : 'Samlet areal'}
                          </span>
                          <strong>
                            {totalVildtagerArea > 0
                              ? number(totalVildtagerArea)
                              : number(totalZoneArea || 0)}{' '}
                            <small>ha</small>
                          </strong>
                          {totalVildtagerArea > 0 && zoneFields.length > 0 && (
                            <span
                              style={{
                                fontSize: 11,
                                opacity: 0.75,
                                display: 'block',
                                marginTop: 2,
                              }}
                            >
                              Områder: {number(totalZoneArea)} ha
                            </span>
                          )}
                        </div>
                        <button className="button primary" onClick={() => setBasket(true)}>
                          Se indkøbsliste <ArrowRight size={17} />
                        </button>
                      </div>
                    </aside>
                    {renderMapComponent()}
                  </div>
                ) : (
                  <div className="empty-state">
                    <Map size={40} />
                    <h2>Start med dit revir</h2>
                    <p>Opret dit revir for at tegne dine første marker.</p>
                    <button
                      className="button primary"
                      onClick={() => {
                        if (allowEstateCreation) {
                          setIsCreateEstateModalOpen(true);
                        } else {
                          setOpenNewEstateOnOverview(true);
                          navigate('overview');
                        }
                      }}
                    >
                      <Plus size={16} /> Opret revir
                    </button>
                  </div>
                )}
                <footer className="workspace-footer">
                  <span>
                    <ShieldCheck size={15} /> {isExample ? 'Eksempeldata · ' : ''}
                    {demo
                      ? 'Rundtur · ændringer og filer gemmes ikke'
                      : user
                        ? cloudDirty || cloudError
                          ? 'Lokale ændringer afventer kontoen'
                          : 'Konto med lokal sikkerhedskopi'
                        : 'Planer gemmes i denne browser'}
                  </span>
                  {isExample ? (
                    <button
                      className="text-button"
                      onClick={() => {
                        if (
                          confirm(
                            'Fjern eksempel-reviret og dets marker? Dine egne revirer og profil bevares.',
                          )
                        ) {
                          setState((s) => {
                            const estates = s.estates.filter((e) => e.id !== 'example-estate');
                            const selectedEstateId = estates[0]?.id ?? '';
                            const plans = s.plans.filter((p) => p.estateId !== 'example-estate');
                            return {
                              ...s,
                              estates,
                              fields: s.fields.filter((f) => f.estateId !== 'example-estate'),
                              plans,
                              activities: s.activities.filter(
                                (a) => a.estateId !== 'example-estate',
                              ),
                              attachments: s.attachments.filter(
                                (a) => a.estateId !== 'example-estate',
                              ),
                              selectedEstateId,
                              selectedPlanId:
                                plans.find((p) => p.estateId === selectedEstateId)?.id ?? '',
                            };
                          });
                          navigate('overview');
                        }
                      }}
                    >
                      Fjern eksempeldata <ArrowRight size={14} />
                    </button>
                  ) : (
                    <button className="text-button" onClick={() => navigate('profile')}>
                      Konto og sikkerhedskopi <ArrowRight size={14} />
                    </button>
                  )}
                </footer>
              </section>
            ) : (
              renderContentPages()
            )}
          </main>
        )}
        {notice && (
          <div className="toast" role="status">
            <Check size={18} />
            <span>{notice}</span>
            <button className="icon-button" aria-label="Luk besked" onClick={() => setNotice('')}>
              <X size={16} />
            </button>
          </div>
        )}
        {basket && (
          <div className="drawer-overlay">
            <button
              className="drawer-scrim"
              aria-label="Luk indkøbsliste"
              onClick={() => setBasket(false)}
            />
            <section
              className="order-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Indkøbsliste"
            >
              <div className="drawer-heading">
                <div>
                  <p className="eyebrow">{estate?.name ?? 'DIN PLAN'}</p>
                  <h2>
                    Din indkøbsliste<span className="title-dot">.</span>
                  </h2>
                </div>
                <button
                  className="icon-button"
                  aria-label="Luk indkøbsliste"
                  onClick={() => setBasket(false)}
                >
                  <X size={22} />
                </button>
              </div>
              <div className="inline-actions" style={{ padding: '0 24px 12px' }}>
                <button
                  className="button secondary"
                  disabled={!selectedLines.length}
                  onClick={() =>
                    downloadOrderCsv(
                      selectedLines.map((line) => ({
                        ...line,
                        packs: getPacks(line.product.id, line.recommendedPacks),
                      })),
                    )
                  }
                >
                  <ShoppingBag size={16} /> CSV
                </button>
                <button className="button secondary" onClick={() => window.print()}>
                  Print indkøbsliste
                </button>
              </div>
              <p className="muted">Frøbehovet samles pr. blanding og rundes op til hele pakker.</p>
              {issues.map((issue) => (
                <p className="inline-error" key={issue}>
                  {issue}
                </p>
              ))}
              <div className="order-lines">
                {lines.length === 0 ? (
                  <div className="empty-state">
                    <Sprout size={32} />
                    <h3>
                      {isFreeAccount && issues.length
                        ? 'Vildtagre afventer frøblanding'
                        : 'Ingen frø valgt endnu'}
                    </h3>
                    <p>
                      {isFreeAccount && issues.length
                        ? 'Dine kladder er gemt. Vælg en gyldig frøblanding til alle vildtagre før indkøb beregnes.'
                        : 'Tegn en mark, og vælg en frøblanding.'}
                    </p>
                    {isFreeAccount && issues.length > 0 && (
                      <button
                        className="button primary"
                        onClick={() => {
                          const field = vildtagerFields.find((f) =>
                            selectionIssues([f], plan, products).some((issue) =>
                              issue.startsWith(`${f.name}:`),
                            ),
                          );
                          setBasket(false);
                          navigate('map');
                          if (field) {
                            setSelectedFieldId(field.id);
                            setBlendFieldId(field.id);
                          }
                        }}
                      >
                        Vælg frøblanding
                      </button>
                    )}
                  </div>
                ) : (
                  lines.map((line) => {
                    const packs = getPacks(line.product.id, line.recommendedPacks);
                    return (
                      <div
                        className={`order-line ${checks[line.product.id] === false ? 'unchecked' : ''}`}
                        key={line.product.id}
                      >
                        <div className="order-line-title">
                          <label className="order-product-check">
                            <input
                              type="checkbox"
                              aria-label={`Vælg ${line.product.name}`}
                              checked={checks[line.product.id] !== false}
                              onChange={(e) =>
                                setChecks((s) => ({ ...s, [line.product.id]: e.target.checked }))
                              }
                            />
                          </label>
                          <span
                            className="product-dot"
                            style={{ background: blendColor(line.product) }}
                          />
                          <div>
                            <h3>{line.product.name}</h3>
                            <p>
                              {number(line.areaHa)} ha ·{' '}
                              {line.product.unit === 'korn'
                                ? `${Math.round(line.needKg).toLocaleString('da-DK')} kerner`
                                : `${number(line.needKg)} kg`}{' '}
                              frøbehov
                            </p>
                          </div>
                          <strong>{money(packs * line.product.priceDkk)}</strong>
                        </div>
                        <div className="order-line-detail">
                          <label>
                            Antal {line.product.unit === 'korn' ? 'poser' : 'pakker'}
                            <input
                              aria-label={`Antal ${line.product.unit === 'korn' ? 'poser' : 'pakker'} ${line.product.name}`}
                              type="number"
                              min={1}
                              max={100000}
                              step={1}
                              value={packs}
                              onChange={(e) => {
                                const value = Number(e.target.value);
                                if (Number.isInteger(value) && value >= 1 && value <= 100000)
                                  setOverrides((s) => ({ ...s, [line.product.id]: value }));
                              }}
                            />
                          </label>
                          <span>
                            {isKernelUnit(line.product.unit)
                              ? `${line.product.packKg.toLocaleString('da-DK')} kerner / pose`
                              : `${line.product.packKg} kg / pakke`}
                            <br />
                            {money(line.product.priceDkk)} / {packWord(1, line.product.unit)}
                          </span>
                        </div>
                        <p className="muted">
                          Anbefalet: {line.recommendedPacks}{' '}
                          {packWord(line.recommendedPacks, line.product.unit)} · Du får{' '}
                          {isKernelUnit(line.product.unit)
                            ? `${Math.round(packs * line.product.packKg).toLocaleString('da-DK')} kerner`
                            : `${number(packs * line.product.packKg)} kg`}
                        </p>
                        {packs * line.product.packKg + 1e-9 < line.needKg ? (
                          <p className="inline-error">
                            Du mangler{' '}
                            {isKernelUnit(line.product.unit)
                              ? `${Math.round(line.needKg - packs * line.product.packKg).toLocaleString('da-DK')} kerner`
                              : `${number(line.needKg - packs * line.product.packKg)} kg`}
                            . Vælg mindst {line.recommendedPacks}{' '}
                            {packWord(line.recommendedPacks, line.product.unit)}.
                          </p>
                        ) : (
                          (() => {
                            const surplus = packs * line.product.packKg - line.needKg;
                            const surplusHa =
                              line.product.rateKgHa > 0 ? surplus / line.product.rateKgHa : 0;
                            const surplusText = isKernelUnit(line.product.unit)
                              ? `${Math.round(surplus).toLocaleString('da-DK')} kerner`
                              : `${number(surplus)} kg`;
                            return (
                              <p className="order-surplus">
                                {surplusText} i overskud
                                {surplusHa >= 0.01
                                  ? ` (rækker til ca. ${number(surplusHa)} ha ekstra)`
                                  : ''}
                              </p>
                            );
                          })()
                        )}
                        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            className="text-button danger"
                            disabled={planReadOnly}
                            style={{
                              fontSize: 12,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                            onClick={() => removeProductFromCart(line.product.id)}
                            title={`Fjern ${line.product.name} fra kurven og nulstil markvalg`}
                          >
                            <Trash2 size={13} /> Fjern fra kurv
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="order-summary">
                <div>
                  <span>Valgte produkter</span>
                  <strong>{money(subtotal)}</strong>
                </div>
                <div className="muted">
                  <span>5 % partner-rabat (kode BEINER)</span>
                  <span>−{money(subtotal * 0.05)}</span>
                </div>
                <div className="summary-total">
                  <span>Total efter rabat</span>
                  <strong>{money(subtotal * 0.95)}</strong>
                </div>
                <button
                  className="button primary full-width"
                  style={{ marginTop: 14, padding: '12px', fontSize: 14, fontWeight: 600 }}
                  disabled={shopBusy || !selectedLines.length}
                  onClick={() => void shopAction(true)}
                >
                  {shopBusy ? 'Gør kurven klar hos Faunamaster…' : 'Gå til kurv hos Faunamaster'}
                </button>
                {shopMessage && (
                  <p role="status" className="notice" style={{ marginTop: 10 }}>
                    {shopMessage}
                  </p>
                )}
                {isGuest && fields.length > 0 && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: '12px 14px',
                      background: 'rgba(22, 101, 52, 0.15)',
                      border: '1px solid rgba(74, 222, 128, 0.3)',
                      borderRadius: 8,
                      textAlign: 'center',
                    }}
                  >
                    <p style={{ margin: '0 0 8px 0', fontSize: 13, color: '#e2e8f0' }}>
                      Vil du gemme din vildtagerplan permanent til næste sæson?
                    </p>
                    <button
                      type="button"
                      className="button secondary full-width"
                      onClick={() => {
                        setBasket(false);
                        setShowSaveGuestModal(true);
                      }}
                      style={{
                        background: '#16a34a',
                        borderColor: '#15803d',
                        color: '#ffffff',
                        fontWeight: 600,
                        fontSize: 13,
                        padding: '8px 12px',
                      }}
                    >
                      Gem min plan (Opret gratis profil)
                    </button>
                  </div>
                )}
                <a
                  href="https://shop.faunamaster.com/discount/BEINER"
                  target="_blank"
                  rel="noreferrer"
                  className="shop-link"
                  onClick={() => {
                    const est = state.estates.find((e) => e.id === selectedEstateId);
                    void recordFaunamasterClick({
                      source: 'shop_link',
                      targetUrl: 'https://shop.faunamaster.com/discount/BEINER',
                      userId: user?.id,
                      userEmail: user?.email,
                      userName: state.profile?.name || undefined,
                      estateId: est?.id,
                      estateName: est?.name,
                    });
                  }}
                >
                  Besøg Faunamaster <ArrowUpRight size={15} />
                </a>
              </div>
            </section>
          </div>
        )}
        {showAdvisorModal && (
          <SeedAdvisorModal
            isOpen={showAdvisorModal}
            onClose={() => {
              setShowAdvisorModal(false);
              setAdvisorTargetFieldId('');
            }}
            products={products}
            fieldAreaHa={(() => {
              const targetId = advisorTargetFieldId || selectedFieldId;
              const targetField = fields.find((f) => f.id === targetId);
              return targetField ? fieldAreaHa(targetField.geometry) : undefined;
            })()}
            fieldName={(() => {
              const targetId = advisorTargetFieldId || selectedFieldId;
              return fields.find((f) => f.id === targetId)?.name;
            })()}
            onAddToCart={(productId, areaHa) => {
              addCustomAreaToCart(productId, areaHa);
            }}
            onSelectAndCloseField={(productId) => {
              const targetId = advisorTargetFieldId || selectedFieldId;
              if (targetId) {
                const prod = products.find((p) => p.id === productId);
                const targetFieldName = fields.find((f) => f.id === targetId)?.name || 'marken';
                setState((s) => {
                  let planList = s.plans;
                  let curPlanId = s.selectedPlanId;
                  if (!planList.length || !planList.some((p) => p.id === curPlanId)) {
                    const fallbackPlan: Plan = {
                      id: curPlanId || id(),
                      estateId: s.selectedEstateId || s.estates[0]?.id || id(),
                      name: 'Vildtager ' + new Date().getFullYear(),
                      year: new Date().getFullYear(),
                      status: 'draft',
                      notes: '',
                      selections: { [targetId]: productId },
                      updatedAt: new Date().toISOString(),
                    };
                    return {
                      ...s,
                      plans: [fallbackPlan],
                      selectedPlanId: fallbackPlan.id,
                    };
                  }
                  return {
                    ...s,
                    plans: planList.map((p) =>
                      p.id === curPlanId
                        ? {
                            ...p,
                            selections: { ...p.selections, [targetId]: productId },
                            updatedAt: new Date().toISOString(),
                          }
                        : p,
                    ),
                  };
                });
                setSelectedFieldId('');
                setAdvisorTargetFieldId('');
                notify(
                  `${prod?.name || 'Frøblandingen'} er tilføjet til indkøbslisten for ${targetFieldName}. Tegn eller vælg næste mark.`,
                );
              }
              setShowAdvisorModal(false);
              setPage('map');
            }}
            onSelectProduct={(productId) => {
              const targetId = advisorTargetFieldId || selectedFieldId;
              if (targetId) {
                const prod = products.find((p) => p.id === productId);
                const targetFieldName = fields.find((f) => f.id === targetId)?.name || 'marken';
                setState((s) => {
                  let planList = s.plans;
                  let curPlanId = s.selectedPlanId;
                  if (!planList.length || !planList.some((p) => p.id === curPlanId)) {
                    const fallbackPlan: Plan = {
                      id: curPlanId || id(),
                      estateId: s.selectedEstateId || s.estates[0]?.id || id(),
                      name: 'Vildtager ' + new Date().getFullYear(),
                      year: new Date().getFullYear(),
                      status: 'draft',
                      notes: '',
                      selections: { [targetId]: productId },
                      updatedAt: new Date().toISOString(),
                    };
                    return {
                      ...s,
                      plans: [fallbackPlan],
                      selectedPlanId: fallbackPlan.id,
                    };
                  }
                  return {
                    ...s,
                    plans: planList.map((p) =>
                      p.id === curPlanId
                        ? {
                            ...p,
                            selections: { ...p.selections, [targetId]: productId },
                            updatedAt: new Date().toISOString(),
                          }
                        : p,
                    ),
                  };
                });
                // Re-open separate field menu so user sees chosen blend and "Tilføj til kurv" button
                setSelectedFieldId(targetId);
                setAdvisorTargetFieldId('');
                notify(
                  `${prod?.name || 'Frøblandingen'} er valgt til ${targetFieldName}. Tryk 'Tilføj til kurv' for at bekræfte.`,
                );
              }
              setShowAdvisorModal(false);
              setPage('map');
            }}
          />
        )}
        {pendingAdvisorInvite && (
          <A11yDialog aria-labelledby="advisor-invite-title" className="advisor-invite-backdrop">
            <div className="advisor-invite-card">
              <div className="advisor-invite-head">
                <div className="advisor-invite-icon">
                  <ShieldCheck size={24} />
                </div>
                <div>
                  <h3 id="advisor-invite-title" className="advisor-invite-title">
                    Rådgiver-invitation
                  </h3>
                  <p className="advisor-invite-sub">Tilknytning af professionel rådgiver</p>
                </div>
              </div>
              <p className="advisor-invite-text">
                <strong>
                  {pendingAdvisorInvite.advisorName ||
                    pendingAdvisorInvite.advisorEmail ||
                    'En professionel rådgiver'}
                </strong>{' '}
                ønsker adgang til at rådgive og hjælpe dig med opmåling og plejeplaner for dine
                revirer.
              </p>
              <div className="advisor-invite-password">
                <label className="advisor-invite-label">Vælg en adgangskode (anbefales)</label>
                <input
                  type="password"
                  placeholder="Mindst 6 tegn (så du altid kan logge ind fremover)"
                  value={invitePassword}
                  onChange={(e) => setInvitePassword(e.target.value)}
                  className="advisor-invite-input"
                />
                <span className="advisor-invite-hint">
                  Gør det nemt at logge ind med din e-mail og adgangskode på alle enheder.
                </span>
              </div>
              <div className="advisor-invite-actions">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await professionalRejectInvitation();
                      setPendingAdvisorInvite(null);
                      notify('Invitationen blev afvist.');
                      if (typeof window !== 'undefined' && window.history.replaceState) {
                        const u = new URL(window.location.href);
                        u.searchParams.delete('accept_advisor');
                        window.history.replaceState({}, '', u.toString());
                      }
                    } catch (err: any) {
                      notify(err?.message || 'Kunne ikke afvise invitation.');
                    }
                  }}
                  className="advisor-invite-btn"
                >
                  Afvis
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      if (invitePassword.trim()) {
                        if (invitePassword.trim().length < 6) {
                          notify('Adgangskoden skal have mindst 6 tegn.');
                          return;
                        }
                        if (supabase) {
                          await supabase.auth.updateUser({ password: invitePassword.trim() });
                        }
                      }
                      await professionalAcceptInvitation(pendingAdvisorInvite.advisorId);
                      setPendingAdvisorInvite(null);
                      notify('Du har godkendt rådgiveren. Velkommen til Plotwise!');
                      if (typeof window !== 'undefined' && window.history.replaceState) {
                        const u = new URL(window.location.href);
                        u.searchParams.delete('accept_advisor');
                        window.history.replaceState({}, '', u.toString());
                      }
                    } catch (err: any) {
                      notify(err?.message || 'Kunne ikke acceptere invitation.');
                    }
                  }}
                  className="advisor-invite-btn is-primary"
                >
                  Accepter rådgiver
                </button>
              </div>
            </div>
          </A11yDialog>
        )}
        <EstateModal
          isOpen={isCreateEstateModalOpen}
          initialEstate="new"
          onClose={() => setIsCreateEstateModalOpen(false)}
          onSave={handleSaveNewEstate}
          allowMutation={allowEstateCreation}
        />
        {upgradeModal?.isOpen && (
          <UpgradeModal
            isOpen={upgradeModal.isOpen}
            onClose={() => setUpgradeModal(null)}
            featureName={upgradeModal.featureName}
            requiredTier={upgradeModal.requiredTier}
            description={upgradeModal.description}
          />
        )}
        <SystemUpdateToast isTourActive={tourActive || showAppUpdateModal || hasAppUpdate} />
        <MobilePwaInstallSheet
          isOpen={pwaSheetOpen && !PWA_INSTALL_SUSPENDED}
          onClose={() => setPwaSheetOpen(false)}
          notify={notify}
        />
        <StandaloneAppUpdateModal
          isOpen={showAppUpdateModal}
          onUpdate={applyAppUpdate}
          onClose={dismissAppUpdateModal}
          isUpdating={isUpdatingApp}
          serverVersion={appUpdateInfo?.serverVersion}
          serverCommit={appUpdateInfo?.serverCommit}
          serverBuildTime={appUpdateInfo?.serverBuildTime}
          clientBuildTime={
            effectiveUpdateTime
              ? String(effectiveUpdateTime)
              : appUpdateInfo?.currentBuildTime || CLIENT_BUILD_TIME
          }
        />

        {guideModalOpen && (
          <UserOnboardingGuide
            isOpen={guideModalOpen}
            onClose={() => setGuideModalOpen(false)}
            userTier={guideUserTier}
            onNavigate={(p) => navigate(p)}
            onToggleFullscreen={() => setMapFullscreen((v) => !v)}
            isFullscreen={mapFullscreen}
            onTriggerDraw={(req) => {
              if (page !== 'map') navigate('map');
              setDrawRequest(req.kind);
            }}
            onOpenUpgradeModal={openUpgradeModal}
            onStartInteractiveTour={handleStartTour}
          />
        )}
        {tourActive && (
          <FloatingTourBar
            currentTier={tourTier}
            stepIndex={tourStep}
            onNext={handleTourNext}
            onPrev={handleTourPrev}
            onClose={() => {
              setTourActive(false);
              onExitTour?.();
            }}
            onAction={handleTourAction}
            isFullscreen={mapFullscreen}
            collapsed={tourCollapsed}
            onToggleCollapse={setTourCollapsed}
            isMobile={isMobile}
            tracking={{
              page,
              fieldCount: fields.filter((f) => f.kind !== 'zone' && !isJagtPoiField(f)).length,
              hasZoneField: fields.some(
                (f) =>
                  f.kind === 'zone' ||
                  (f.areaType && f.areaType !== 'mark' && f.areaType !== 'vildtagre'),
              ),
              hasPoi: fields.some((f) => isJagtPoiField(f)),
              cadastralEnabled,
              markblokkeEnabled,
              selectedFieldHasSeed: Boolean(selectedFieldId && planSelections[selectedFieldId]),
              cartCount: Object.keys(planSelections).length,
              isDrawing: Boolean(isDrawingMap || drawRequest !== null),
            }}
          />
        )}

        <TermsConsentModal
          isOpen={Boolean(
            user &&
            user.id !== 'demo' &&
            !showMfa &&
            state.profile.terms_accepted_at == null &&
            (typeof navigator === 'undefined' || !navigator.webdriver) &&
            (typeof sessionStorage === 'undefined' ||
              sessionStorage.getItem('account-seeded') !== '1') &&
            !user.email?.endsWith('.invalid') &&
            !user.email?.endsWith('.test') &&
            !user.email?.includes('eksempel.dk'),
          )}
          onAccept={async (analyticsConsent) => {
            try {
              await acceptUserTerms(analyticsConsent);
            } catch {
              // fallback locally
            }
            setState((prev) => ({
              ...prev,
              profile: {
                ...prev.profile,
                terms_accepted_at: new Date().toISOString(),
                analytics_consent: analyticsConsent,
              },
            }));
          }}
          onOpenTerms={() => {
            setLegalDoc('terms');
            setLegalModalOpen(true);
          }}
          onOpenPrivacy={() => {
            setLegalDoc('privacy');
            setLegalModalOpen(true);
          }}
        />

        <MarketingConsentPrompt
          isOpen={Boolean(
            user &&
            user.id !== 'demo' &&
            !showMfa &&
            state.profile.terms_accepted_at != null &&
            state.profile.marketing_consent_at == null &&
            (typeof navigator === 'undefined' || !navigator.webdriver) &&
            !user.email?.endsWith('.invalid') &&
            !user.email?.endsWith('.test') &&
            !user.email?.includes('eksempel.dk'),
          )}
          onAnswer={async (consent) => {
            await setMarketingConsent(consent);
            setState((prev) => ({
              ...prev,
              profile: {
                ...prev.profile,
                marketing_consent: consent,
                marketing_consent_at: new Date().toISOString(),
              },
            }));
          }}
        />

        <LegalTermsModal
          initialDoc={legalDoc}
          isOpen={legalModalOpen}
          onClose={() => setLegalModalOpen(false)}
        />

        <SaveGuestPlanModal
          isOpen={showSaveGuestModal}
          onClose={() => setShowSaveGuestModal(false)}
          onSuccess={() => {
            notify('Din vildtagerplan er nu overført til din profil!');
          }}
          fieldCount={fields.length}
        />
      </div>
    </Suspense>
  );

  return appContent;
}

export default function App({ demo }: { demo?: DemoOptions } = {}) {
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourSessionKey, setTourSessionKey] = useState(0);
  const [showLandingLogin, setShowLandingLogin] = useState(false);
  const [returnedFromTour, setReturnedFromTour] = useState(false);

  const isTour = Boolean(demo || tourActive);
  const currentTourStep = TOUR_STEPS[tourStep];
  const effectiveDemo: DemoOptions | undefined = demo
    ? demo
    : tourActive
      ? {
          page: currentTourStep?.page || 'overview',
          basket: currentTourStep?.basket,
        }
      : undefined;

  const handleStartTour = () => {
    setTourActive(true);
    setTourStep(0);
    setShowLandingLogin(false);
    setReturnedFromTour(false);
  };

  const handleExitTour = () => {
    setTourActive(false);
    setTourStep(0);
    setShowLandingLogin(false);
    setReturnedFromTour(true);
  };

  const handleLoginFromTour = () => {
    setTourActive(false);
    setTourStep(0);
    setShowLandingLogin(true);
    setReturnedFromTour(false);
  };

  const handleResetTour = () => {
    setTourStep(0);
    setTourSessionKey((k) => k + 1);
  };

  const handleTourStepChange = (newStep: number) => {
    setTourStep(newStep);
  };

  return (
    <AuthProvider key={isTour ? `auth-demo-${tourSessionKey}` : 'auth-live'} demo={effectiveDemo}>
      <WorkspaceProvider
        key={isTour ? `workspace-demo-${tourSessionKey}` : 'workspace-live'}
        demo={effectiveDemo}
      >
        {tourActive ? (
          <ProductTour
            onExit={handleExitTour}
            onLogin={handleLoginFromTour}
            step={tourStep}
            onStepChange={handleTourStepChange}
            onReset={handleResetTour}
          >
            <AppShell demo={effectiveDemo} initialTourActive={false} onExitTour={handleExitTour} />
          </ProductTour>
        ) : (
          <AppShell
            demo={demo}
            onStartTour={handleStartTour}
            initialShowLogin={showLandingLogin}
            initialTourActive={false}
            returnedFromTour={returnedFromTour}
          />
        )}
      </WorkspaceProvider>
    </AuthProvider>
  );
}
